/** TanStack Query hooks over the SolumClient — the metamodel, the semantic layer, and entity rows. */
import { useMemo } from 'react'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  SolumTransportError,
  SUBJECT_MAX_ROWS,
  combineOrGroups,
  groupMissingCondition,
  groupOtherFilters,
  groupValueFilter,
  lookupSearchDeclared,
  resolveReadProvider,
  type EntityReadCapabilities,
  type ReadProvider,
  type PgFilter,
  type EntityIdentityInfo,
  type EntityTypeInfo,
  type ExperienceKind,
  type Row,
  type SelectOptions,
  type SolumClient,
} from '@solum/sdk'
import type { NavDocument as NavDoc } from '@solum/ui'
import { useSolum } from './client'
import { invalidateLookups } from './useLookupSearch'
import { isListable, recordNaming, unnamedRecordTitle } from './recordIdentity'
import { usePersona, personaForResolve } from '../app/persona'
import { parseResolvedExperience, resolveTablePresentationEnvelope, parseDashboardChartDefinition,
  GROUP_MISSING, GROUP_OTHER, orderedGroupKeys, planGroupedPage, type GroupDomain,
  type TablePresentation, type DescribedItem, type ChartDefinition } from '@solum/experience'
import { readDashboardItemRows, type DashboardSourceColumn } from '@solum/sdk'

/** What a Console tile renders. Mirrors the Portal's shape exactly. */
export interface DashboardItemContent {
  columns: DashboardSourceColumn[]
  rows: Record<string, unknown>[]
  chart?: ChartDefinition
}

/**
 * Invalidate EVERY cached read of an entity's rows, whatever key family it sits
 * under — `rows`, `rows-page`, `rows-count`, and anything added later.
 *
 * #113 introduced `rows-page`/`rows-count` for the server-side list, and every
 * mutation in this file was still invalidating only `['rows', entity]`. So a
 * write committed and the list kept showing the old value: the browser E2E saw
 * an inline edit "commit" and the cell still read Lost.
 *
 * A prefix predicate rather than three literal keys, because the next key family
 * would silently miss again — and a stale list after a successful write is the
 * kind of bug users report as "it didn't save".
 */
export function invalidateEntityRows(qc: ReturnType<typeof useQueryClient>, entity: string) {
  return qc.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey as unknown[]
      return typeof k[0] === 'string' && (k[0] as string).startsWith('rows') && k[1] === entity
    },
  })
}

/**
 * Invalidate every CACHED SURFACE that can be showing a record (#2018).
 *
 * ── the defect this closes ─────────────────────────────────────────────────
 * `useSaveEntity` invalidated three families — rows, the record, and titles —
 * out of the eight a saved record can appear in. So a rename left the
 * reference label on another record's form, the dashboard count, and the
 * related-count badge all reading the pre-edit value until a reload. Measured
 * on main: `ref-labels` and `dashboard` had ZERO invalidators anywhere in this
 * host, though both are read.
 *
 * ── why some families are scoped and others are not ────────────────────────
 * Scoping is decided by the KEY, not by preference. A key that carries the
 * entity can be narrowed; one that carries an opaque batch cannot:
 *
 *   scoped     rows* · [row, entity, id] · [lookup-labels, entity]
 *              [related-counts, entity, id] · [related-order, entity]
 *   family     [record-titles, refs] · [ref-labels, want] — the key is a LIST
 *              of refs from many entities, so there is nothing to narrow to
 *              without reading every cached key and guessing at its contents
 *   family     [dashboard, host, placement] · [measures, keys] — a measure is
 *              an aggregate; the record that moved it is not in the key
 *
 * Widening one of the last two to "only if it mentions this entity" would mean
 * inspecting cached VALUES to decide invalidation, which is how a cache starts
 * lying about a record it has not fetched yet.
 *
 * ── `work` is opt-in, and that is deliberate ───────────────────────────────
 * `my-work` is the caller's own queue. Whether a save can move a record into
 * or out of it is a property of the ENTITY, which this client does not know —
 * so it is not guessed. Callers that do know (a claim, a status change) pass
 * `{ work: true }`, exactly as `usePickUpWork` already did by hand.
 */
export function invalidateRecordSurfaces(
  qc: ReturnType<typeof useQueryClient>,
  entity: string,
  id?: string,
  also: { work?: boolean } = {},
) {
  /* Every invalidation is COLLECTED and returned (#2000 audit). It used to
     `void` all of them, so a caller awaiting this hook resolved while the
     lists it had just invalidated were still refetching — a save could close
     its form over a grid that had not caught up. The promise `invalidateQueries`
     returns settles when the ACTIVE refetches do, which is exactly the visible
     set. */
  const settling: Promise<void>[] = []
  const family = (key: string) => { settling.push(qc.invalidateQueries({ queryKey: [key] })) }

  // Every row family — rows, rows-page, rows-count, and whatever comes next.
  settling.push(invalidateEntityRows(qc, entity))

  // The record itself. Narrowed to the id when the caller knows it: a create
  // does not, and must still refresh the entity is list.
  settling.push(qc.invalidateQueries({ queryKey: id ? ['row', entity, id] : ['row', entity] }))

  // Anything that RENDERS this record somewhere else by name.
  family('record-titles')
  family('ref-labels')
  settling.push(qc.invalidateQueries({ queryKey: ['lookup-labels', entity] }))
  /* #2217's shared Lookup result family. This belongs at the generic mutation
     boundary, not at individual forms: every successful entity write can make
     a cached picker answer stale. It is collected with the other promises so
     mutateAsync does not close over a picker cache that still tells the old
     truth. */
  settling.push(invalidateLookups(qc, entity))

  // What the record may be OFFERED next (#1131). An act usually moves a status
  // and a status usually changes which acts are legal, so leaving this cached
  // is how a surface goes on offering a verb the server has just closed —
  // exactly the affordance the action kernel exists to stop.
  void qc.invalidateQueries({ queryKey: ['entity-actions', entity] })

  // Aggregates that can count it.
  family('dashboard')
  family('measures')

  // Relations, only where the key can name the record.
  if (id) settling.push(qc.invalidateQueries({ queryKey: ['related-counts', entity, id], exact: true }))
  settling.push(qc.invalidateQueries({ queryKey: ['related-order', entity] }))

  if (also.work) family('my-work')
  return Promise.all(settling).then(() => undefined)
}

/**
 * Await ONE record's relationship read model back into agreement (#2000).
 *
 * `invalidateRecordSurfaces` above answers a different question and answers it
 * well: *what else on screen can be showing this record*. It is deliberately
 * broad, deliberately fire-and-forget, and it is scoped to the FAR entity a
 * write touched. Neither property is wrong there, and both are wrong here.
 *
 * ── the defect this closes ───────────────────────────────────────────────
 * A relationship write moves TWO reads that have to agree: the rows of the
 * band, and the count in its heading. Every writer invalidated them with a
 * bare `void`, so a caller could not tell the difference between "the server
 * accepted this" and "the surface now shows it". Close a picker on the first
 * and the row appears a beat later, or does not appear at all — and the
 * count above it disagrees with the list below it until a reload.
 *
 * So this returns the CONVERGENCE promise. A caller that awaits it is holding
 * its form open until the surface is honest, which is the whole point; a
 * caller that ignores it is exactly as broken as it was before, and now
 * visibly so.
 *
 * ── why the rows are matched by PREDICATE ────────────────────────────────
 * A related list is cached as `['related', schema, table, where]` and the
 * `where` is what names the record — `from_id`, `to_id`, or the fk column,
 * depending on the slot's storage (`relationRead`). There is no prefix that
 * selects "this record's related lists" and only those: the record id lives in
 * the LAST key segment, not the first.
 *
 * Invalidating the `['related']` family instead would refetch every related
 * list on screen, including those of a record nobody touched — which is what
 * the writers did before, and what makes an unrelated band flicker on a save.
 * Reading the `where` is not inspecting cached VALUES (the thing
 * `invalidateRecordSurfaces` above refuses to do): a `where` is part of the
 * KEY, and the key is the question, not the answer.
 */
export function reconcileRelationships(
  qc: ReturnType<typeof useQueryClient>,
  entity: string,
  recordId: string,
): Promise<void> {
  if (!entity || !recordId) return Promise.resolve()
  return Promise.all([
    /* Every related projection whose FILTER names this record — and only under
       a column that can NAME one. Matching any value equal to the id matched a
       plain string column that happened to hold the same text; an edge read is
       identified by `rel_type` plus an endpoint, and an fk read by a column
       whose name ends `_id`. Anything else is a coincidence, not a relation. */
    qc.invalidateQueries({
      predicate: (q) => {
        const k = q.queryKey as unknown[]
        if (k[0] !== 'related') return false
        const where = k[3]
        if (!where || typeof where !== 'object') return false
        const w = where as Record<string, unknown>
        if (k[1] === 'core' && k[2] === 'relationship') {
          /* The endpoint AND the kind of record it is. `relationRead` puts this
             record's own type in the filter, so an edge read identifies the
             slot completely: relationship, direction, far entity, self entity.
             Matching on the uuid alone reconciled any entity's record that
             happened to share it (#2000 audit). */
          if (typeof w.rel_type !== 'string') return false
          if (w.from_id === recordId) return w.from_type === entity
          if (w.to_id === recordId) return w.to_type === entity
          return false
        }
        return Object.entries(w).some(([col, v]) => v === recordId && col.endsWith('_id'))
      },
    }),
    // The heading's number. EXACT: a prefix would sweep any longer key that
    // happened to start here, which is a different question than this one.
    qc.invalidateQueries({ queryKey: ['related-counts', entity, recordId], exact: true }),
  ]).then(() => undefined)
}

export function useSchema() {
  const { client } = useSolum()
  return useQuery({ queryKey: ['schema'], queryFn: () => client.describeSchema() })
}

/**
 * The registry row for an entity, off the one globally cached schema query —
 * the row the SDK builds a handle FROM, so its `read_audit` and `write_door`
 * decide the route rather than anything this host knows about the entity.
 */
export function useEntityInfo(entity: string): EntityTypeInfo | null {
  const schema = useSchema()
  return useMemo(
    () => ((schema.data?.entities ?? []) as EntityTypeInfo[]).find((e) => e.key === entity) ?? null,
    [schema.data, entity])
}

/**
 * How this entity is READ (#2759, ADR F-96) — resolved by the SDK from the
 * registry row, never by anything this host knows about the entity:
 *
 *   · `direct_table`     — the ordinary RLS read: PostgREST filters, counts,
 *                          grouping, 500-row pages;
 *   · `governed_subject` — the subject doors (#2444 slice 4): a CLOSED
 *                          vocabulary, 200-row pages, no count, and every row
 *                          returned is a `record.viewed`;
 *   · `module_door`      — its module's own doors through the core
 *                          dispatchers: the vocabulary its module DECLARED,
 *                          its own page bound, an exact total only where a
 *                          count is declared, search only where declared.
 *
 * `governed` is true for both of the last two: a surface asks only what the
 * provider can answer and SAYS so where it has to give something up — it never
 * decides who may read (the provider does that), and it never names a module.
 */
export interface ReadRoute {
  provider: ReadProvider
  governed: boolean
  /** The provider's page bound, or null for an unbounded table read. */
  pageLimit: number | null
  /** A module door's declared capabilities; null otherwise. */
  capabilities: EntityReadCapabilities | null
  /** Whether a list can be drawn at all (false: nothing reads this table for an API caller). */
  readable: boolean
}

export function readRouteOf(info: EntityTypeInfo | null | undefined): ReadRoute {
  const provider = resolveReadProvider(info)
  const capabilities = provider === 'module_door' ? info!.read_contract!.capabilities! : null
  return {
    provider,
    governed: provider !== 'direct_table',
    pageLimit: provider === 'governed_subject' ? SUBJECT_MAX_ROWS : capabilities ? capabilities.max_page : null,
    capabilities,
    readable: info?.read_contract?.readable ?? true,
  }
}

export function useReadRoute(entity: string): ReadRoute {
  const info = useEntityInfo(entity)
  return useMemo(() => readRouteOf(info), [info])
}

/**
 * Is this entity read through a GOVERNED provider (#2444 subject doors, #2759
 * module door) rather than its table? A host reads this to keep to the
 * provider's closed vocabulary and bounded page — see `useReadRoute`.
 */
export function useIsGovernedRead(entity: string): boolean {
  return useReadRoute(entity).governed
}

/** The largest page any governed provider serves (the subject doors' bound; a module door's is at most this). */
export const GOVERNED_PAGE_SIZE = SUBJECT_MAX_ROWS

/**
 * What a person reads when they type into a picker over a subject entity whose
 * module registers no search door. Words for a person — the SDK's own refusal
 * names tables and operators, which is for a developer.
 */
export const GOVERNED_SEARCH_ONLY = 'These records are searched only through their governed search, and none is registered here.'

/**
 * Words typed into a picker over a MODULE-DOOR entity (#2759): its module's
 * declared lookup search, through `core.entity_lookup` — ranked and bounded by
 * the module, never a fetched page filtered in the browser. Null where the
 * provider declares no PICKER search (`lookup_search`, #2915 L — page search is
 * a separate capability; the caller then says GOVERNED_SEARCH_ONLY).
 */
export async function governedWordSearch(
  raw: Pick<ReturnType<SolumClient['raw']>, 'rpcTable'>, route: ReadRoute, entity: string, query: string, limit: number,
): Promise<{ id: string; title: string | null }[] | null> {
  if (route.provider !== 'module_door' || !lookupSearchDeclared(route.capabilities) || !route.capabilities?.titles) return null
  const rows = await raw.rpcTable<{ id: string; title: string | null }>('entity_lookup',
    { p_entity: entity, p_ids: null, p_search: query.trim(), p_limit: Math.min(limit, route.capabilities.max_page) }, 'core')
  return Array.isArray(rows) ? rows : []
}

/**
 * The governed acts this caller may be OFFERED on this record (#1131).
 *
 * `enabled` is deliberately narrow: a brand-new record has no row to ask
 * about, and asking anyway would put a per-record authority question against
 * an id the server has never seen.
 *
 * Nothing downstream may treat `canAct` as permission — it decides what to
 * render, and the verb refuses on its own regardless (ruling 40821 point 4).
 */
export function useEntityActions(
  entity: string | undefined,
  id?: string,
  opts: { enabled?: boolean } = {},
) {
  const { client } = useSolum()
  const isRecord = id != null && id !== 'new'
  return useQuery({
    queryKey: ['entity-actions', entity ?? '', isRecord ? id : ''],
    enabled: !!entity && (opts.enabled ?? true),
    queryFn: () => client.myEntityActions(entity!, isRecord ? id : undefined),
  })
}

/** Persona-aware entity nouns/icons in one request; no screen owns a parallel map. */
export function useEntityIdentities(persona?: string) {
  const { client } = useSolum()
  const active = usePersona()
  const forPersona = personaForResolve(persona, active)
  return useQuery<EntityIdentityInfo[]>({
    queryKey: ['entity-identities', forPersona ?? ''],
    queryFn: () => client.describeEntityIdentities(forPersona ?? undefined),
  })
}

export function useSemantics(entity?: string, enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['semantics', entity ?? '*'],
    queryFn: () => client.describeSemantics(entity),
    enabled,
  })
}


/**
 * The navigation, resolved by the CASCADE (#511): a user's own pin, else their
 * persona's default, else the system default. `core.navigation()` does the
 * resolving — the console must not re-implement precedence, or the two will
 * disagree about which nav somebody sees and only one of them is the one that
 * was configured.
 *
 * Returns null on a deployment that has not been migrated to the nav experience
 * yet; callers fall back to the built-in groups rather than showing nothing.
 */
export type { NavDocument, NavGroup, NavItem } from '@solum/ui'

/**
 * `host` is what lets ONE object serve two clients (#511). The console's bar and
 * the portal's are different documents on the same cascade; asking without a
 * host is asking for whichever one happened to be written last. It was never
 * passed until now, and `nav.customer_member` — two console routes — was
 * therefore also the answer the portal got.
 */
export const CONSOLE_HOST = 'console'

/**
 * May THIS caller write THIS record? (#2031 prerequisite, ruled in mailbox
 * #1958 comment 62517.)
 *
 * ── WHY A ROUND TRIP AT ALL ────────────────────────────────────────────────
 * Until this existed, the generic record editor derived read-only from the
 * entity's WRITE DOOR alone — a structural property of the entity, identical
 * for every principal. So a person who could SEE a record but not write it was
 * offered an editable form whose Save the server then refused. Root's ruling is
 * blunt about that shape: "editable-then-refused is not acceptable UX".
 *
 * The answer already exists in the database and is already reachable:
 * `core.can_act(text, uuid, text)` is revoked from `public` and `anon` and left
 * to `authenticated` (2026082118:171). Nothing asked it.
 *
 * ── IT FAILS CLOSED, AND THAT IS THE WHOLE CONTRACT ────────────────────────
 * The ruling: "Pending/refused capability fails closed and is named; never
 * flash editable." So this returns a THREE-state answer rather than a boolean,
 * and the caller treats anything that is not a positive `true` as read-only:
 *
 *   'may'      — the door answered true. The only editable state.
 *   'may-not'  — the door answered false. Read-only, and the reason is named.
 *   'unknown'  — still loading, or the door raised, or the deployment has no
 *                such function. Read-only WHILE IT IS UNKNOWN.
 *
 * A boolean cannot express that: `false` would conflate "refused" with "not
 * back yet", and a hook that defaulted to `true` while loading would render an
 * editable form for one frame and then take it away, which is the flash the
 * ruling forbids. Distinguishing them also lets the host say WHICH it is, so a
 * person is not told "you may not" when the truth is "I could not ask".
 *
 * ── WHY NOT SIMPLY TRUST RLS ───────────────────────────────────────────────
 * RLS is still the enforcement and stays the enforcement — this hook changes
 * only what the console DRAWS. The live two-actor suite keeps asserting the
 * transport refusal directly, as defence in depth, for exactly that reason: if
 * this read were ever wrong in the permissive direction, the database must
 * still refuse. A capability read is a UX contract, never an authority.
 */
export type WriteCapability = 'may' | 'may-not' | 'unknown'

/**
 * The PostgREST ABI of `core.can_act(p_type text, p_id uuid, p_action text)`.
 *
 * Keep the names executable rather than documenting them beside the call: an
 * unknown named argument makes PostgREST answer 404, which the fail-closed
 * capability contract correctly turns into `unknown` and can therefore look
 * like an ordinary refusal in the browser.
 */
export function writeCapabilityArgs(entity: string, id: string) {
  return { p_type: entity, p_id: id, p_action: 'write' }
}

export function useWriteCapability(entity: string, id: string | undefined, enabled = true) {
  const { client } = useSolum()
  const q = useQuery({
    queryKey: ['can-act', entity, id],
    enabled: enabled && !!id && id !== 'new',
    queryFn: () => client.raw().rpcScalar<boolean | null>(
      'can_act', writeCapabilityArgs(entity, id!), 'core'),
    staleTime: 30_000,
    // One retry only. A capability that cannot be read is READ-ONLY, and the
    // sooner the host can say so the shorter the window in which it shows
    // nothing useful; retrying five times would leave the form in `unknown` —
    // correctly read-only, but silently — for seconds.
    retry: 1,
  })
  /* A CREATE form is not a record anybody can be refused: there is no row to
     act on yet, and the create door is a different question the list route
     already answers. Positive by construction rather than by a query that would
     have to be given a null id. */
  /* THE ONLY ANSWER THIS HOOK GIVES, and that is deliberate.
     It used to return `isLoading` and `error` beside the capability, and the
     console's own query-state ratchet was right to red on it: a caller reading
     `isLoading` to decide a render is a caller that can draw the unresolved
     state as a resolved one. There is nothing left to decide — 'unknown'
     already IS loading-or-unreachable, and the contract is that unknown fails
     closed. Exposing the raw states beside it would offer a second, weaker way
     to ask the same question, and the weaker way is the one that eventually
     gets used. */
  return { capability: writeCapabilityOf(id, q.data) }
}

/**
 * The three-state mapping, EXTRACTED so it has a witness.
 *
 * It lived inline until a mutation run measured that nothing could see it:
 * defaulting the capability to writable — the exact failure the ruling forbids —
 * came back GREEN against `apps/console/src/form`, because that package has no
 * jsdom and the fixture answers `can_act` true, so both the correct and the
 * broken mapping produce 'may'. The only witness was the live gate, which does
 * not run here.
 *
 * A pure function is the smallest thing that makes the contract checkable
 * locally, and the contract is the whole point: anything that is not a positive
 * `true` is NOT permission to write.
 */
export function writeCapabilityOf(id: string | undefined, answer: boolean | null | undefined): WriteCapability {
  // A create form is not a record anybody can be refused: there is no row to
  // act on, and the create door is a different question the list route answers.
  if (!id || id === 'new') return 'may'
  if (answer === true) return 'may'
  if (answer === false) return 'may-not'
  // loading, unreachable, or a deployment with no such function — read-only
  // WHILE IT IS UNKNOWN, which is what "fails closed" means here.
  return 'unknown'
}

export function useNavigation() {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['navigation', CONSOLE_HOST],
    queryFn: async () => {
      try {
        return await client.raw().rpcScalar<NavDoc | null>('navigation', { p_host: CONSOLE_HOST }, 'core')
      } catch {
        // A deployment older than core 0.150 has no core.navigation(). That is
        // not an error worth a boundary — it means "use the built-in nav", and
        // the caller already knows how.
        return null
      }
    },
    staleTime: 60_000,
  })
}

/**
 * The caller's OWN pin, or null — the provenance `navigation()` deliberately
 * does not carry.
 *
 * Without it a personalised bar is indistinguishable from an inherited one, and
 * a person who reordered their sidebar once has no way back: "· customised" and
 * a one-click reset both hang off this answer.
 */
export function useNavigationPin() {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['navigation-pin'],
    queryFn: async () => {
      try { return await client.raw().rpcScalar<NavDoc | null>('my_navigation_pin', {}, 'core') }
      catch { return null }
    },
    staleTime: 60_000,
  })
}

/** Pin the caller's own bar, or `null` to go back to whatever they are given. */
export function usePinNavigation() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (doc: NavDoc | null) =>
      client.raw().rpcScalar<void>('pin_navigation', { p_document: doc }, 'core'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['navigation', CONSOLE_HOST] })
      void qc.invalidateQueries({ queryKey: ['navigation-pin'] })
    },
  })
}

/* ── the caller's OWN profile: the SECOND HOST of one verb, not a second door ─
 *
 * `core.my_profile()` / `core.set_my_profile()` (core 2026081719) were put in
 * `core` rather than in `portal` for a reason recorded at the time
 * (apps/portal/src/data.tsx:3477-3480): editing your own display name is every
 * principal on every deployment and *"the CONSOLE needs the identical verb, so a
 * portal-only door would have been the first half of two implementations of one
 * rule."*
 *
 * The console then never called it. Measured on this fork before #1466 slice A:
 * `grep -rn "my_profile" apps/console/src | wc -l` -> 0, while `Topbar.tsx:70`
 * rendered `session.user.email` as an inert `<span>` — the one fact about
 * yourself that this host could show and not change. These two hooks are that
 * call, in the shape this file already uses for `pin_navigation` above.
 */
export function useMyProfile() {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['console', 'my-profile'],
    queryFn: async () => {
      const rows = await client.raw().rpcTable<Row>('my_profile', {}, 'core')
      // At most one row, and always the caller's: the predicate lives inside the
      // definer and takes no parameter, so this cannot be pointed at anybody
      // else. In fixture mode the transport's unknown-function fallthrough
      // returns [] (fixture/transport.ts:1226), so this resolves to null rather
      // than throwing — the menu's session-email fallback is what renders there.
      return rows[0] ?? null
    },
  })
}

/**
 * Save the name. The refusal is the SERVER's sentence — a federated principal is
 * told their directory owns it, a suspended one is told their account is not
 * active — and this host paraphrases neither, for the same reason the portal
 * does not: it owns neither rule and would go stale the day one moved.
 */
export function useSetMyProfile() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (displayName: string) =>
      client.raw().rpcScalar<void>('set_my_profile', { p_display_name: displayName }, 'core'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['console', 'my-profile'] }) },
  })
}

/** One computed measure as the server sees it, under the caller's own RLS. */
export interface MeasureValue {
  key: string
  label: string
  aggregation: string
  format: string | null
  value: number | null
  /** Set when THIS measure failed; the rest of the dashboard still renders. */
  error: string | null
}

/**
 * Dashboard figures, computed SERVER-SIDE (core 0117).
 *
 * The Overview used to fetch rows and reduce them in JavaScript — over a page
 * capped at 500. On any deployment with more than 500 opportunities every figure
 * was wrong, and wrong in the worst way: plausible, precise, and silently
 * truncated. Nobody goes looking for a number that appears to be there
 * (TEC-05 / UX-04).
 */
/**
 * The console's resolved dashboard for a placement (#1188).
 *
 * One call. `core.describe_dashboard` resolves the rung, applies this caller's
 * own layout patch and answers each item's availability server-side — so which
 * figures the console home shows stopped being a literal in Home.tsx and became
 * a row a deployment can override at its tenant or persona rung.
 *
 * The document is read by `parseDescribedDashboard` in `@solum/experience`, the
 * one reader shared with the portal, so the two hosts cannot drift into two
 * interpretations of the same wire format.
 */
export function useConsoleDashboard(placement = 'home') {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['dashboard', CONSOLE_HOST, placement],
    queryFn: () => client.raw().rpcScalar<unknown>(
      'describe_dashboard', { p_host: CONSOLE_HOST, p_placement: placement }, 'core'),
  })
}

/** One caller-RLS page resolver query. Its result is read only by the shared
 * `parseDescribedPage` boundary; this hook makes no availability decision. */
export function useConsolePage(pageKey: string) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['page-composition', CONSOLE_HOST, pageKey],
    queryFn: () => client.describePage(CONSOLE_HOST, pageKey),
  })
}

/**
 * One query per data-bearing tile, through the SHARED governed executor
 * (ROOT 68246 item 6).
 *
 * The Console had no list/chart reader at all, so a governed list or chart
 * composed onto a Console page rendered empty. It now calls exactly what the
 * Portal calls — `readDashboardItemRows` in `@solum/sdk` — so neither host can
 * grow its own read door, its own bounds or its own refusals.
 *
 * The key carries the tile's real source identity AND `definitionVersion`, so a
 * composition that changed cannot serve rows read for the source it replaced —
 * a stale row here would be a governed answer to a question nobody asked.
 */
export function useDashboardItemContents(items: readonly DescribedItem[]) {
  const { client } = useSolum()
  return useQueries({
    queries: items.map(item => ({
      queryKey: [
        'console', 'dashboard-item', item.itemKey, item.kind,
        item.source.viewId ?? item.source.sourceKey ?? null, item.definitionVersion,
        item.definition,
      ],
      enabled: item.state === 'ready' && (item.kind === 'list' || item.kind === 'chart'),
      queryFn: async (): Promise<DashboardItemContent | null> => {
        const read = await readDashboardItemRows(client, {
          kind: item.kind,
          viewId: item.source.viewId,
          sourceKey: item.source.sourceKey,
        })
        if (!read) return null
        if (item.kind !== 'chart') return read
        // Validated AND bound to the source this widget names.
        return { ...read, chart: parseDashboardChartDefinition(item.definition, item.source.sourceKey!) }
      },
      staleTime: 30_000,
    })),
  })
}

export function useMeasures(keys?: string[]) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['measures', (keys ?? []).join(',')],
    enabled: keys === undefined || keys.length > 0,
    queryFn: () => client.raw().rpcTable<MeasureValue>('dashboard_measures',
      keys ? { p_keys: keys } : {}, 'core'),
  })
}

/**
 * The default page size. It was an unnamed literal `500` repeated across this
 * file, which is how the console came to truncate every list silently: a bare
 * number reads as a sensible performance guard, and nothing downstream could
 * tell "500 rows" from "the first 500 of 4,000" (#110).
 */
export const PAGE_SIZE = 500

/**
 * A CAPPED client-side page of an entity. Every call site is a decision.
 *
 * Reach for one of these first — each is complete where this one is not:
 *   a list a user reads as "all records"  →  `useEntityPage` (server page + total)
 *   a headline number / KPI tile          →  `useMeasures` or `useEntityCount`
 *   a reference column's display label    →  `useLookupLabels` (keyed by id)
 *
 * What is left over is a genuinely bounded pool — a picker, a palette fallback —
 * and it must SAY that it is bounded. `apps/console/src/data/entity-reads.test.ts`
 * is the ratchet: every surviving call site is named there with the reason it is
 * allowed to be capped, and a new undeclared one fails the suite (TEC-02 / UX-01).
 */
export function useEntityRows(entity: string, limit: number = PAGE_SIZE, enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['rows', entity, limit],
    // `enabled` so a caller that has switched to the server-side page does not
    // ALSO pull a client-side page it will discard. Passing limit: 0 would still
    // have cost a round trip.
    enabled,
    queryFn: async () => {
      const handle = await client.entity(entity)
      if (!handle) return []
      // A subject entity's door refuses a page above 200 rather than clamping
      // it. This pool is a declared CAPPED pool already (see above and
      // entity-reads.test.ts), so its cap is the door's — and every row in it
      // is a `record.viewed`, which is one more reason to keep it small.
      return handle.list({ limit: handle.pageLimit != null ? Math.min(limit, handle.pageLimit) : limit })
    },
  })
}

/**
 * A page of an entity PLUS the total that matches — so a surface can say
 * "1–500 of 4,000" and make the next range reachable instead of quietly showing
 * 500 and letting the user believe that is all there is (#882).
 */
export interface PageQuery {
  limit?: number
  offset?: number
  /** PostgREST-native filters — already translated and verified faithful (#113). */
  filters?: { key: string; value: string }[]
  /** PostgREST order clause, e.g. 'issue_date.desc.nullslast'. */
  order?: string
  /** Free-text search a MODULE DOOR declares (#2759); refused, never ignored, by any other provider. */
  search?: string
  /** false ⇒ do not fetch at all. For a surface whose scope does not apply — the
   *  drawer's Insights on a person, say — where the alternative is fetching the
   *  whole book to then render nothing. */
  enabled?: boolean
}

/**
 * A page of an entity, filtered and sorted BY THE SERVER, plus the total.
 *
 * The count is deliberately NOT requested up front. `Prefer: count=exact` costs a
 * full scan under RLS — measured at 56 ms of a 128 ms request on 204,560 rows —
 * and it is pure waste in the common case, because **a page that comes back
 * shorter than its limit already tells you the total exactly.** So the count is
 * a second, count-only query fired only when the first page comes back FULL:
 *
 *   list fits in a page  →  1 request,  no count cost at all
 *   list is truncated    →  2 requests, the second ~25 ms and off the render path
 *
 * The alternative — always asking for the count — makes every small list pay for
 * the rare large one.
 */
export function useEntityPage(entity: string, q: PageQuery = {}) {
  const { client } = useSolum()
  const { limit = PAGE_SIZE, offset = 0, filters, order, search, enabled = true } = q

  // Resolved from the SCHEMA, which is one globally-cached query the shell already
  // holds — not a per-entity `core.entity_type` lookup. describe_schema() already
  // carries schema_name/table_name for all 106 entities, so that lookup was a
  // redundant round trip SERIALISED in front of every list load: the page could
  // not start until it came back.
  const schema = useSchema()
  const info = useMemo(
    () => ((schema.data?.entities ?? []) as EntityTypeInfo[]).find((x) => x.key === entity) ?? null,
    [schema.data, entity])
  const table = info
  /* A GOVERNED entity (#2444 subject doors, #2759 module door) is paged through
     its provider, whose page is bounded — a paging parameter, not a result
     restriction: the page says which range it holds and the pager offers the
     next one. Its total is the PROVIDER's envelope: exact where a module
     declares a count, else known only when a page comes back short (the
     subject doors never count: a count would disclose rows to count them), and
     `GridPager` draws the open range. Only a table read pays for a count. */
  const route = readRouteOf(info)
  const governed = route.governed
  const subject = route.provider === 'governed_subject'
  const pageLimit = route.pageLimit != null ? Math.min(limit, route.pageLimit) : limit

  const page = useQuery({
    queryKey: ['rows-page', entity, pageLimit, offset, filters ?? null, order ?? null, search ?? null],
    enabled: enabled && table != null,
    // Keep the rendered grid mounted while the adjacent page travels. Besides
    // avoiding a blank flash, this preserves keyboard focus on the pager. Carry
    // the offset WITH the rows so the retained page is never labelled as the
    // newly requested range before that request has actually landed (#882).
    // Retain only an ADJACENT PAGE of the same query. Keeping data across a
    // filter/order change would put rows from one governed view underneath the
    // labels and actions of another while its request travels.
    placeholderData: (previous, previousQuery) => {
      const previousKey = previousQuery?.queryKey
      const sameQuery = previousKey?.[0] === 'rows-page'
        && previousKey[1] === entity
        && previousKey[2] === pageLimit
        && JSON.stringify(previousKey[4]) === JSON.stringify(filters ?? null)
        && previousKey[5] === (order ?? null)
        && previousKey[6] === (search ?? null)
      return sameQuery ? previous : undefined
    },
    // Through the registry handle: its PROVIDER decides the route, and a read
    // the provider cannot express rejects with a typed refusal that the
    // surface's boundary names, rather than reaching the wire. A governed page
    // carries the provider's own total.
    queryFn: async () => {
      const h = client.handleFor(info!)
      if (!governed) return { rows: await h.list<Row>({ limit: pageLimit, offset, filters, order, ...(search ? { search } : {}) }), offset, total: undefined }
      const p = await h.listPage<Row>({ limit: pageLimit, offset, filters, order, ...(search ? { search } : {}) })
      return { rows: p.rows, offset, total: p.total ?? undefined }
    },
  })

  const full = (page.data?.rows.length ?? 0) >= pageLimit
  const count = useQuery({
    queryKey: ['rows-count', entity, filters ?? null],
    enabled: enabled && table != null && full && !governed,
    queryFn: async () => (await client.raw().selectPage<Row>(
      table!.schema_name, table!.table_name, { limit: 1, filters })).total,
  })

  const pre = prerequisiteState(enabled, table != null, schema)

  return {
    ...page,
    isLoading: page.isLoading || pre.isLoading,
    isError: page.isError || pre.isError,
    error: page.error ?? pre.error,
    // Both, because either can be the reason there is nothing on screen.
    refetch: () => { void schema.refetch(); return page.refetch() },
    data: {
      ...pageResult(page.data?.rows, pageLimit, page.data?.offset ?? offset, governed ? page.data?.total : count.data),
      offset: page.data?.offset ?? offset,
      /** The page size actually asked for — a governed entity's is its provider's. */
      limit: pageLimit,
      /** Read through the governed subject doors: no count, 200-row pages. */
      subject,
      /** Read through a governed provider (subject doors or a module door). */
      governed,
    },
  }
}

export interface GroupedPageQuery {
  /** The view's own PostgREST filters, already translated faithfully (#113). */
  filters: { key: string; value: string }[]
  /** The search's or-group body (`searchCondition`), or null. */
  searchGroup: string | null
  /** The ENUMERABLE field's domain (ADR F-84 §2). */
  domain: GroupDomain
  direction: 'asc' | 'desc'
  /** The remaining sorts — they order rows WITHIN a group. */
  order?: string
  offset: number
  limit: number
  enabled?: boolean
}

/**
 * A FLAT page of a list GROUPED by one enumerable field, with every group's
 * WHOLE-result total (#2375, ADR F-84 §2).
 *
 *   1. one bounded count per declared value, plus Other values and Not
 *      provided — each the ordinary `count=exact` read, under the caller's RLS,
 *      with the view's filters and the search applied;
 *   2. `planGroupedPage` maps the flat range onto per-group slices;
 *   3. each slice is read with its group's condition and the remaining sorts,
 *      `id` last so an offset is stable.
 *
 * So a group that crosses a page boundary is simply the next slice of the same
 * group, and its header carries the same total — nothing here groups a fetched
 * page. The domain is enumerable, so the number of counts is bounded by it. A
 * SUBJECT entity is never grouped here: its door has no count by design.
 */
export function useGroupedEntityPage(entity: string, q: GroupedPageQuery) {
  const { client } = useSolum()
  const schema = useSchema()
  const info = useMemo(
    () => ((schema.data?.entities ?? []) as EntityTypeInfo[]).find((x) => x.key === entity) ?? null,
    [schema.data, entity])
  // Grouping needs a count per group: only a TABLE read can state one (contract v1 has no grouping operation).
  const enabled = (q.enabled ?? true) && info != null && readRouteOf(info).provider === 'direct_table'
  const column = q.domain.column
  const keys = orderedGroupKeys(q.domain, q.direction)
  const declared = q.domain.values.map((value) => value.value)
  const condition = (key: string): { filters: PgFilter[]; orGroup: string | null } => {
    if (key === GROUP_MISSING) return groupMissingCondition(column, q.domain.textual)
    if (key === GROUP_OTHER) return { filters: groupOtherFilters(column, declared, q.domain.textual), orGroup: null }
    const value = q.domain.values.find((candidate) => candidate.key === key)!.value
    return { filters: [groupValueFilter(column, value)], orGroup: null }
  }
  const filtersFor = (key: string): PgFilter[] => {
    const own = condition(key)
    return [...q.filters, ...own.filters, ...combineOrGroups([q.searchGroup, own.orGroup])]
  }
  const scope = [q.filters, q.searchGroup, column] as const

  const counts = useQueries({
    queries: keys.map((key) => ({
      queryKey: ['rows-group-count', entity, ...scope, key],
      enabled,
      queryFn: async () => (await client.raw().selectPage<Row>(
        info!.schema_name, info!.table_name, { limit: 1, filters: filtersFor(key) })).total ?? 0,
    })),
  })
  const countsReady = counts.every((c) => c.data != null)
  const totals = countsReady ? keys.map((key, i) => ({ key, total: counts[i]!.data as number })) : null
  const plan = totals ? planGroupedPage(totals, q.offset, q.limit) : null
  const order = [q.order, 'id.asc'].filter(Boolean).join(',')

  const slices = useQueries({
    queries: (plan?.slices ?? []).map((slice) => ({
      queryKey: ['rows-group-slice', entity, ...scope, slice.key, slice.offset, slice.limit, order],
      enabled,
      queryFn: async () => client.handleFor(info!).list<Row>({
        limit: slice.limit, offset: slice.offset, filters: filtersFor(slice.key), order,
      }),
    })),
  })

  const all = [...counts, ...slices]
  const pre = prerequisiteState(q.enabled ?? true, info != null, schema)
  const rows = slices.every((s) => s.data) ? slices.flatMap((s) => s.data ?? []) : []
  return {
    isLoading: pre.isLoading || (enabled && (!countsReady || slices.some((s) => s.isLoading))),
    isError: pre.isError || all.some((s) => s.isError),
    error: pre.error ?? all.find((s) => s.isError)?.error ?? null,
    isFetching: all.some((s) => s.isFetching),
    refetch: () => { void schema.refetch(); for (const s of all) void s.refetch() },
    data: {
      rows,
      totals: new Map((totals ?? []).map((group) => [group.key, group.total])),
      total: plan?.total ?? null,
      continued: plan?.continued ?? new Set<string>(),
      offset: q.offset,
      limit: q.limit,
    },
  }
}

/**
 * The state a page query is in BEFORE it is allowed to start — as a pure
 * function, so it can be tested (the same reason `pageResult` is one).
 *
 * `useEntityPage` cannot fire until the schema resolves the entity's table, so
 * until then its query is DISABLED — and a disabled TanStack v5 query reports
 * `isLoading: false` with no data, while `pageResult` rebuilds `data` into a
 * non-null empty result. A <QueryBoundary> watching the hook therefore saw
 * neither pending, nor error, nor absent data: it called the surface READY and
 * the list drew ZERO ROWS before a single row had been asked for. That is the
 * exact failure QueryBoundary exists to prevent (#385), and without this the fix
 * that pointed list boundaries at this hook would have MOVED the bug rather than
 * removed it — into a narrower window and a harder one to see.
 *
 * Two deliberate non-behaviours:
 *   • gated on `schema.isLoading`, not on `table == null` alone — once the schema
 *     has SETTLED without the entity, this hook behaves exactly as it always did,
 *     rather than acquiring a new eternal spinner
 *   • `enabled: false` is a caller declining ("this scope does not apply" — the
 *     drawer's Insights on a person), not a caller waiting. It reports neither.
 */
export function prerequisiteState(
  enabled: boolean,
  tableResolved: boolean,
  schema: { isLoading: boolean; isError: boolean; error?: unknown },
): { isLoading: boolean; isError: boolean; error: unknown } {
  const waiting = enabled && !tableResolved
  return {
    isLoading: waiting && schema.isLoading,
    isError: waiting && schema.isError,
    error: waiting ? (schema.error ?? null) : null,
  }
}

/**
 * The truncation decision, as a pure function — extracted so it can be TESTED
 * against a dataset bigger than the cap. That is the whole exit criterion for
 * TEC-02: past `limit` rows, a surface must come back complete or say it did
 * not, and a test over ten rows proves nothing about a five-hundred-row cap.
 *
 *   short page  ⇒ the page IS the answer; total is free and exact
 *   full page   ⇒ total comes from the count query; `truncated` says so
 */
export function pageResult(
  rows: Row[] | undefined, limit: number, offset: number, count: number | null | undefined,
): { rows: Row[]; total: number | null; truncated: boolean } {
  const got = rows ?? []
  const full = got.length >= limit
  const total = full ? (count ?? null) : got.length + offset
  return { rows: got, total, truncated: total != null && total > got.length + offset }
}

/** entity → its physical `{schema_name, table_name}`, off the one globally cached
 *  schema query the shell already holds. Extracted from `useEntityPage` so the
 *  count/lookup paths below resolve a table the same way, without a second
 *  `core.entity_type` round trip serialised in front of every read. */
export function useEntityTable(entity: string): { schema_name: string; table_name: string } | null {
  const schema = useSchema()
  return useMemo(() => {
    const e = ((schema.data?.entities ?? []) as { key: string; schema_name: string; table_name: string }[])
      .find((x) => x.key === entity)
    return e ? { schema_name: e.schema_name, table_name: e.table_name } : null
  }, [schema.data, entity])
}

/**
 * The EXACT number of rows an entity has (under the caller's RLS), computed by
 * the server and costing one row of transfer.
 *
 * This exists because `rows.filter(…).length` over a 500-row page is not a slow
 * count, it is a WRONG one — and a KPI tile has nowhere to put "…of the first
 * 500 I happened to load". `Prefer: count=exact` is paid for deliberately here:
 * a headline figure is exactly the case where the full scan is worth it, which
 * is the opposite of the list path (see `useEntityPage`).
 */
export function useEntityCount(entity: string, filters?: { key: string; value: string }[], enabled = true) {
  const { client } = useSolum()
  const table = useEntityTable(entity)
  /* A SUBJECT entity has no count (#2444): its rows are disclosed only through
     a door that records each one, and a count read would have to disclose them
     to count them. The query stays disabled, so `data` is undefined and every
     caller already renders that as ABSENT (`—`), never as zero. A MODULE-DOOR
     entity counts through its handle (#2759): exact where its module declares
     a count, else null — also rendered absent. */
  const info = useEntityInfo(entity)
  const route = readRouteOf(info)
  return useQuery({
    queryKey: ['rows-count', entity, filters ?? null],
    enabled: enabled && table != null && route.provider !== 'governed_subject',
    queryFn: async () => route.provider === 'module_door'
      ? (await client.handleFor(info!).listPage<Row>({ limit: 1, filters })).total
      : (await client.raw().selectPage<Row>(table!.schema_name, table!.table_name, { limit: 1, filters })).total,
  })
}

/** The display label of a party/record row, in the order the console has always
 *  resolved it. One definition, because six files had their own copy and they
 *  disagreed about whether a missing name renders as `—`, `Unassigned` or the id.
 *
 *  `typeLabel` is the entity's governed SINGULAR label, for a caller holding a
 *  row it can definitely read: an organization saved with neither a legal nor a
 *  display name is `Unnamed organization`, which is the ruled sentence
 *  (`recordIdentity.ts`, #1705/#2325 §3) for an authorized record whose title
 *  expression produced nothing. Omitted, the answer is exactly what it has
 *  always been, so no existing caller moves. */
export function displayLabel(r: Row, typeLabel?: string | null): string {
  const person = `${r.given_name ?? ''} ${r.family_name ?? ''}`.trim()
  // `title` is the name column for the record-shaped entities (an engagement, a
  // tender, an agreement) that carry no name/display_name — without it they
  // rendered as their uuid in every picker.
  const named = r.display_name ?? r.legal_name ?? r.name ?? r.title ?? (person || null)
  if (named != null && String(named).trim() !== '') return String(named)
  return typeLabel ? unnamedRecordTitle(typeLabel) : 'Record unavailable'
}

/**
 * The rows among `ids` of a registered entity, read through its REGISTRY
 * handle (#2444 slice 4) — for a query function that is not a hook.
 *
 * Exactly the ids asked about, batched: `id=in.(…)` for an ordinary entity and
 * `core.subject_get_many` for a subject one, where each RETURNED row is one
 * `record.viewed` and a row the caller may not see is simply absent. An
 * unregistered entity answers nothing rather than guessing a table.
 */
export async function readRowsById(client: SolumClient, entity: string, ids: readonly unknown[]): Promise<Row[]> {
  const wanted = [...new Set(ids.filter((id) => id != null && id !== '' && id !== 'null').map(String))]
  if (wanted.length === 0) return []
  const handle = await client.entity(entity)
  return handle ? handle.getMany<Row>(wanted) : []
}

/**
 * Resolve reference columns to labels by fetching EXACTLY the rows referenced.
 *
 * The console's habit was to pull `useEntityRows('core_organization')` — a 500-row
 * page — and `.find()` the account of each row on screen. Past 500 organizations
 * that does not truncate a list, it prints the WRONG THING in a column: an invoice
 * for a real customer renders as "Unassigned" because the customer sorted 501st.
 * There is no chrome that can honestly describe that, so it is not capped — it is
 * keyed. `id=in.(…)` over the ids actually on the page is complete by construction
 * and bounded by the page, not by the table.
 */
export function useLookupLabels(entity: string, ids: unknown[]) {
  const { client } = useSolum()
  const table = useEntityInfo(entity)
  // Sorted + deduped so the query key is stable across renders that reorder the
  // same page; otherwise every re-render is a cache miss and a fresh round trip.
  const wanted = useMemo(() => {
    const set = new Set<string>()
    for (const id of ids) if (id != null && id !== '') set.add(String(id))
    return [...set].sort()
  }, [ids])

  const q = useQuery({
    queryKey: ['lookup-labels', entity, wanted],
    enabled: table != null && wanted.length > 0,
    // `getMany` is exactly the ids on the page, batched — `id=in.(…)` in chunks
    // of 100 for an ordinary entity, and `core.subject_get_many` (200 per POST,
    // one `record.viewed` per row RETURNED) for a subject one (#2444).
    queryFn: () => client.handleFor(table!).getMany<Row>(wanted),
  })

  const byId = useMemo(
    () => new Map(((q.data ?? []) as Row[]).map((r) => [String(r.id), r])),
    [q.data])

  /** label for an id — `fallback` while the fetch is in flight or the row is gone. */
  const label = useMemo(() => (id: unknown, fallback = '—'): string => {
    if (id == null || id === '') return fallback
    const r = byId.get(String(id))
    return r ? displayLabel(r) : fallback
  }, [byId])

  return { label, byId, query: q }
}

/**
 * What each reference COLUMN of an entity points at, from the METAMODEL (#1993).
 *
 * `core.describe_reference_targets` derives this from `core.relationship_type`
 * (storage='fk') and its registered endpoint pairs. Before it, the only source
 * of that fact in the console was `ListConfig.refs` — a hand-written map that a
 * repository census found on exactly FOUR pages, all saying
 * `{ account_id: 'core_organization' }`. Every other registered view resolved
 * nothing, which is how an ordinary reference cell came to render its UUID.
 *
 * A column the metamodel does not claim is ABSENT here, and that is the point:
 * the caller renders neutral copy rather than the id. Measured on the fleet, 89
 * of the 153 reference-shaped columns visible in a registered view are
 * derivable; the other 64 point at `auth.users` or at rows no module declared,
 * and inventing a target for them would name the wrong record confidently.
 */
export interface ReferenceTarget {
  entity_type: string
  column_name: string
  relationship_key: string
  label: string
  /** the single registered endpoint, or null when the column is polymorphic */
  target_entity_type: string | null
  /** for a polymorphic column: the column holding the per-row target type */
  discriminator_column: string | null
  /** Exact registered discriminator-token to entity mapping; never inferred from row data. */
  discriminator_targets: { discriminator_value: string | null; target_entity_type: string }[]
}

export function useReferenceTargets(entity: string | undefined, enabled = true) {
  const { client } = useSolum()
  const query = useQuery({
    queryKey: ['reference-targets', entity ?? ''],
    enabled: enabled && Boolean(entity),
    queryFn: () => client.raw().rpcTable<ReferenceTarget>(
      'describe_reference_targets', { p_entity: entity }, 'core'),
  })
  const byColumn = useMemo(
    () => new Map((query.data ?? []).map((t) => [t.column_name, t])),
    [query.data])
  return { byColumn, query }
}

/**
 * The many-column reference resolver for a grid — governed end to end (#1993).
 *
 * TARGET from `core.describe_reference_targets`, LABEL from `core.titles_of`.
 * Neither is a column-name guess, and that is the whole change: this hook used
 * to select the target rows itself and run `displayLabel(row)` over them —
 * `display_name ?? legal_name ?? name ?? title` — which is the guess
 * `core.entity_title` exists to replace (#1472 measured 120 of 262 entities
 * with no such column at all) and read the target table instead of the door.
 *
 * `core.titles_of` is SECURITY INVOKER, so a record this session cannot see
 * simply contributes no row — indistinguishable from an id that does not exist.
 * The resolver therefore returns its FALLBACK for missing, deleted and hidden
 * targets alike, and callers must never make that fallback the id.
 *
 * ONE query for every reference column on the page, so the number of hooks does
 * not depend on how many columns a view or a ListConfig declares — a config
 * that gains a column mid-session would otherwise change the hook count between
 * renders and React would tear the list down with "rendered more hooks than
 * during the previous render". (ListView already carries a scar from exactly
 * that; see the `activeRow` comment there.)
 *
 * `refs` stays supported and WINS: #1993 keeps explicit page renderers as
 * presentation overrides, so a desk that wants a different target for a column
 * still says so — it is just no longer required for correctness.
 */
export function useRefLabels(
  entity: string | undefined,
  refs: Record<string, string> | undefined,
  rows: Row[],
  columns?: string[],
) {
  const derived = useReferenceTargets(entity)

  /**
   * The target entity type of a value in `column`, for `row`.
   *
   * Polymorphic columns need the row: the target is held in a discriminator
   * column beside the id, and the registered token-to-target map is authoritative.
   * A row carrying an unknown or ambiguous token resolves to nothing and renders
   * neutral — row data never chooses which entity type `titles_of` reads.
   */
  const resolveType = useMemo(() => (column: string, row?: Row): string | null => {
    const override = (refs ?? {})[column]
    const target = derived.byColumn.get(column)
    if (!target) return null
    if (target.target_entity_type) {
      return !override || override === target.target_entity_type ? target.target_entity_type : null
    }
    if (!target.discriminator_column || !row) return null
    const declared = row[target.discriminator_column]
    const token = declared == null || declared === '' ? null : String(declared)
    const matches = target.discriminator_targets.filter((candidate) => candidate.discriminator_value === token)
    if (matches.length !== 1) return null
    const resolved = matches[0]!.target_entity_type
    return !override || override === resolved ? resolved : null
  }, [refs, derived.byColumn])

  /** every (type, id) the page actually references, deduped */
  const wanted = useMemo(() => {
    const consider = new Set<string>([...Object.keys(refs ?? {}), ...(columns ?? [])])
    if (!columns) for (const key of derived.byColumn.keys()) consider.add(key)
    const seen = new Set<string>()
    const out: { type: string; id: string }[] = []
    for (const row of rows) {
      for (const column of consider) {
        const value = row[column]
        if (value == null || value === '') continue
        const type = resolveType(column, row)
        if (!type) continue
        const key = `${type}:${String(value)}`
        if (seen.has(key)) continue
        seen.add(key)
        out.push({ type, id: String(value) })
      }
    }
    return out
  }, [rows, refs, columns, derived.byColumn, resolveType])

  const titles = useRecordTitles(wanted)

  return useMemo(() => (column: string, id: unknown, fallback = '—', row?: Row): string => {
    if (id == null || id === '') return fallback
    const type = resolveType(column, row)
    if (!type) return fallback
    return titles.byRef.get(`${type}:${String(id)}`)?.title ?? fallback
  }, [resolveType, titles.byRef])
}

interface FieldAccessDeclaration { via_api: boolean }

/**
 * Read one row without turning an unrelated table revoke into a second door.
 *
 * A field ACL deliberately removes table-level SELECT when any physical column
 * is not allowed through the API. The ordinary `select=*` must remain first:
 * it is the cheap, canonical read for every entity without such a declaration.
 * Only its authenticated privilege refusal (403) earns a metadata check. If
 * that check proves at least one physical field is absent from API rows,
 * `core.read_fields` is the already-governed projection; otherwise the original
 * refusal is rethrown byte-for-byte rather than being laundered into success.
 */
export async function readEntityRow(client: SolumClient, entity: string, id: string): Promise<Row | null> {
  const handle = await client.entity(entity)
  if (!handle) return null
  try {
    // `get`: one row by id — `core.subject_get` on a subject entity (#2444),
    // which is the single `record.viewed` the deleted client-side
    // `useRecordReadAudit` used to fire best-effort after the fact.
    return await handle.get<Row>(id)
  } catch (error) {
    if (!(error instanceof SolumTransportError) || error.status !== 403) throw error
    // A GOVERNED provider (subject doors, module door) already projects exactly
    // the readable columns; a refusal from it is not the field-ACL case below,
    // and `read_fields` must never become a second door to its rows.
    if (handle.subject || handle.provider === 'module_door') throw error

    let access: FieldAccessDeclaration[]
    try {
      access = await client.raw().rpcTable<FieldAccessDeclaration>(
        'field_access', { p_entity_type: entity }, 'core')
    } catch {
      // No declaration means no authority to reinterpret the direct refusal.
      throw error
    }
    if (!access.some((field) => field.via_api === false)) throw error

    return await client.raw().rpcScalar<Row>(
      'read_fields', { p_entity_type: entity, p_id: id, p_columns: null }, 'core')
  }
}

export function useEntityRow(entity: string, id: string, enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['row', entity, id],
    enabled: enabled && id !== 'new',
    queryFn: () => readEntityRow(client, entity, id),
  })
}

/** Resolve the L1 experience document for one host (null ⇒ L0 auto layout). */
export function useExperienceAtHost(
  entity: string,
  kind: ExperienceKind,
  host: 'console' | 'portal',
  enabled = true,
) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['experience-resolution', entity, `document:${kind}`, host],
    queryFn: async () => {
      const resolved = parseResolvedExperience(
        await client.resolveExperience(entity, `document:${kind}`, { host }),
      )
      if (resolved.state === 'refused') {
        throw new Error(resolved.issues.join('; ') || 'The experience document was refused')
      }
      return resolved.effective?.document ?? null
    },
    enabled,
  })
}

/** Resolve the console's own L1 document. */
export function useExperience(entity: string, kind: ExperienceKind) {
  return useExperienceAtHost(entity, kind, 'console')
}

/**
 * The CANONICAL form (#1466) — artifact `form`, not `document:form`.
 *
 * These are two different stores and `core/2026091105` keeps their names
 * disjoint on purpose: `form` is `core.form`, the registry 347-odd modules
 * write to with `register_form`; `document:form` is the LayoutDocument overlay
 * in `core.experience`. `useExperienceAtHost` prefixes `document:`, so every
 * existing caller asks for the overlay — which is why the registry was
 * resolvable from the server but read by nothing.
 *
 * Absent is an ANSWER, not a failure: an entity with no registered form falls
 * back to L0 field-meta generation, which is what most entities do today.
 */
/**
 * Server verdicts for a LayoutDocument's `when` references (#1466).
 *
 * One call for every rule the document names, with the record in context. The
 * browser never evaluates a condition: `when` carries an opaque registry key
 * and `core.layout_rule_verdicts` answers show / hide / unresolved against
 * `core.rule.condition`, read as the caller.
 *
 * Disabled when the document references nothing, because an empty request is
 * an empty answer and a round trip for it is a round trip for nothing. The
 * host treats "no verdict" as unresolved either way, which is what makes that
 * safe.
 */
export function useLayoutRuleVerdicts(entity: string, recordId: string | null, rules: readonly string[]) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['layout-rule-verdicts', entity, recordId, [...rules].sort().join(',')],
    enabled: rules.length > 0,
    queryFn: () => client.raw().rpcTable<{ rule: string; verdict: 'show' | 'hide' | 'unresolved' }>(
      'layout_rule_verdicts',
      { p_entity: entity, p_record_id: recordId, p_rules: [...rules] }, 'core'),
  })
}

export function useCanonicalForm(entity: string, enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['experience-resolution', entity, 'form'],
    queryFn: async () => {
      const resolved = parseResolvedExperience(
        await client.resolveExperience(entity, 'form', {}),
      )
      if (resolved.state === 'refused') {
        // A refused canonical form is NOT an empty one. Rendering L0 here would
        // silently replace a governed definition with a generated guess.
        throw new Error(resolved.issues.join('; ') || 'The form definition was refused')
      }
      return resolved.effective?.document ?? null
    },
    enabled,
  })
}

/** The RESOLVED experience bundle (core 0042 describe_experience): the {view, form,
 *  card, lookup} definitions for an entity+persona — a stored FormDef here overrides
 *  the field_meta-derived form. null form ⇒ fall back to L0 (auto from field_meta). */
export interface ResolvedExperience {
  entity: string
  form?: { fields?: unknown[] } | null
  view?: unknown; card?: unknown; lookup?: unknown
  /** The table-presentation declaration, or null when the entity declares none
   *  (#1982). `null` is an answer, not an error: the host falls back to grid,
   *  which is `resolveTablePresentation`'s documented behaviour. */
  presentation?: unknown
  /** The GOVERNED resolution — available/default/active/provenance (#1982,
   *  core 2026090915). `presentation` above is the raw stored document and is
   *  kept because #2116's path reads it; this is the answer to act on. */
  presentation_resolved?: unknown
}
/**
 * Ordinary reads omit `previewPersona`: the server derives the caller's active
 * assignment, so a host cannot invent or cross-select somebody else's persona
 * (#2032). The optional argument is retained solely for the platform-admin
 * preview ceremony; the server refuses it for every ordinary principal.
 *
 * Preview remains per-request rather than server-held, so two administrator
 * tabs can inspect different personas without changing each other's context.
 * Its value stays in the query key to keep those explicit previews isolated.
 */
export function useResolvedExperience(entity: string, previewPersona?: string) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['experience-bundle', entity, 'console', previewPersona ?? 'assigned'],
    queryFn: () => client.raw().rpcScalar<ResolvedExperience>('describe_experience', {
      p_entity: entity, p_persona: previewPersona ?? null, p_host: 'console',
    }, 'core'),
  })
}

/**
 * The RESOLVED table presentation for one entity and persona (#1982).
 *
 * `core.describe_experience` answers the stored declaration — or null — and the
 * shared `resolveTablePresentation` turns that into what this host can actually
 * draw. It never returns nothing: an absent, invalid or unsupported declaration
 * resolves to the grid with `status: 'fallback'` and named `issues`, which is
 * the contract `#1987` shipped and the reason a broken definition can never
 * produce a blank surface.
 *
 * `supported` is the host's renderer inventory, not a preference. The console
 * draws all four; a host that draws fewer passes fewer and gets a diagnostic for
 * the rest rather than an empty lens.
 */
export function useTablePresentation(entity: string, supported?: readonly TablePresentation[]) {
  const resolved = useResolvedExperience(entity)
  return useMemo(() => {
    /* THE SERVER RESOLVES, THIS HOST NARROWS (#1982 closure, core 2026090915).
       This used to call `resolveTablePresentation` on the raw document, which
       meant the browser decided what was available and what the default was —
       and could not answer the one question a Table Manager is about: WHICH
       LAYER decided. `presentation_resolved` is the governed answer; the
       `supported` list is this build's renderer inventory, which is the only
       half the database has no business knowing. */
    const r = resolveTablePresentationEnvelope(resolved.data?.presentation_resolved, supported)
    return {
      ...r,
      /* The query's own states, carried through rather than collapsed into the
         resolved value. The first version of this returned only the document,
         so a failed or still-running resolve was indistinguishable from an
         entity that declares nothing — both looked like "grid, resolved". */
      isLoading: resolved.isLoading,
      error: resolved.error ? String((resolved.error as Error).message ?? resolved.error) : null,
      /* True when there is genuinely no declaration, as opposed to one that
         failed to resolve. The host says different things about the two. */
      undeclared: !resolved.isLoading && !resolved.error
        && (resolved.data?.presentation_resolved as { provenance?: { source?: string } } | undefined)
             ?.provenance?.source === 'fallback',
    }
  }, [resolved.data?.presentation_resolved, resolved.isLoading, resolved.error, supported])
}

/** Resolve the selected saved view's lens through the same governed server
 * door as the default. A saved-view kind is a request, not authority. */
export function useRequestedTablePresentation(
  entity: string,
  persona: string | null,
  requested: TablePresentation | null,
  supported?: readonly TablePresentation[],
) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['table-presentation-request', entity, persona, requested],
    enabled: requested != null,
    retry: false,
    queryFn: () => client.raw().rpcScalar('resolve_table_presentation', {
      p_entity: entity, p_persona: persona, p_host: 'console', p_requested: requested,
    }, 'core'),
    select: (wire) => resolveTablePresentationEnvelope(wire, supported),
  })
}

/** A governed read of a graph/reference table (core.relationship, party_role,
 *  contact_point, app_user, party_role_type, activity.activity) — the raw seam a
 *  host uses to compose a party profile + timeline. RLS still applies. */
export function useRelated(
  schema: string, table: string, where: Row, enabled = true,
  opts?: { order?: string; limit?: number },
) {
  const { client } = useSolum()
  /* A registered entity's table is read through its registry handle, so the
     row's own `read_audit` decides the route (#2444): a SUBJECT table goes
     through `core.subject_list`, in pages of at most 200, and its read waits
     for the registry rather than guessing — a guess would be a direct read the
     database refuses. Any other table is the ordinary 500-row raw read. */
  const registry = useSchema()
  const info = useMemo(
    () => ((registry.data?.entities ?? []) as EntityTypeInfo[])
      .find((e) => e.schema_name === schema && e.table_name === table) ?? null,
    [registry.data, schema, table])
  const route = readRouteOf(info)
  return useQuery({
    queryKey: opts ? ['related', schema, table, where, opts] : ['related', schema, table, where],
    enabled: enabled && !registry.isPending,
    queryFn: () => route.governed
      ? client.handleFor(info!).list({ where, limit: Math.min(opts?.limit ?? route.pageLimit!, route.pageLimit!), order: opts?.order })
      : client.raw().select(schema, table, { where, limit: opts?.limit ?? 500, order: opts?.order } satisfies SelectOptions),
  })
}

/** The work-surface aggregating read (core.my_work, core 2026080923): everything
 *  assigned to the caller across every module that DECLARED its entity as work.
 *  SECURITY INVOKER on the server — it runs under the caller's own RLS, so an
 *  assignment to a row the caller cannot see never appears here (R6/#974). */
/**
 * ONE engagement's phase ladder, through the governed door (#2021).
 *
 * `afd.my_project_phases(uuid)` joins the per-project STATE on `afd.phase` to
 * the METHOD vocabulary on `afd.phase_spec`. The cockpit used to read those
 * words off `afd.phase` directly — `name`, `thinking_mode`, `entry_gate`,
 * `exit_gate` and a `state` column — and **none of the five exists there**. The
 * console fixture invented them, so the surface looked right in every test and
 * would have rendered an unlabelled rail against a real PostgREST.
 *
 * Reach is `core.can_see('afd_project', …)` inside the definer, so a project the
 * caller cannot see returns no rows — the same answer an absent project gives,
 * deliberately.
 */
export function useProjectPhases(projectId: string, enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['afd', 'project-phases', projectId],
    enabled: enabled && !!projectId,
    queryFn: () => client.raw().rpcTable<Row>('my_project_phases', { p_project_id: projectId }, 'afd'),
  })
}

/**
 * `afd.my_project_gate_conditions` — ONE engagement's gate conditions (#2020).
 *
 * The cockpit used to read `useRelated('afd','gate_condition',{})` — every
 * condition row the caller can see, across every project they can see — and
 * narrow it to this project's gates in JavaScript. `gate_condition` has no
 * project column (afd 0004:151); it hangs off the GATE, and one engagement's
 * conditions span seven gate ids, so no single PostgREST equality filter
 * expresses the question. The governed door joins the gate and asks
 * `core.can_see('afd_project', …)` server-side instead.
 *
 * Pass `gateId` to ask the single-gate question the checklist asks; omit it for
 * the panel's question, everything open on this engagement.
 */
export function useProjectGateConditions(projectId: string, gateId?: string, enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['afd', 'project-gate-conditions', projectId, gateId ?? null],
    enabled: enabled && !!projectId,
    queryFn: () => client.raw().rpcTable<Row>(
      'my_project_gate_conditions',
      { p_project_id: projectId, p_gate_id: gateId ?? null }, 'afd'),
  })
}

/** The host-neutral AFD ProcessBar wire; clients render its stages verbatim. */
export function useProjectFlowPosition(projectId: string, enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['afd', 'project-flow-position', projectId],
    enabled: enabled && !!projectId,
    queryFn: () => client.raw().rpcScalar<Row | null>(
      'project_flow_position', { p_project_id: projectId }, 'afd'),
  })
}

/**
 * The AFD method's phase vocabulary — `afd.phase_spec`, which is reference data
 * with a `read_all` policy and is deliberately NOT project-scoped.
 *
 * It exists so that no surface has to carry its own copy of the seven words.
 * Two did (`ProjectsList`, `PhaseFeedback`), each with a hand-written
 * `Record<string, string>`, and the fixture carried a third.
 */
export function usePhaseVocabulary() {
  return useRelated('afd', 'phase_spec', {})
}

export function useMyWork(enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['my-work'],
    enabled,
    queryFn: () => client.raw().rpcTable<Row>('my_work', {}, 'core'),
  })
}

export interface WorkQueueSummary {
  id: string
  key: string
  name: string
  team_id: string
  open_items: number
}

/** The caller's queue pools and their visible, claimable backlog. The database
 *  counts each registered work host under the caller's own RLS; a queue does
 *  not widen record visibility merely because the caller belongs to its team. */
export function useWorkQueues(enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['work-queues'],
    enabled,
    queryFn: () => client.raw().rpcTable<WorkQueueSummary>('queues_i_can_work', {}, 'core'),
  })
}

/** The one queue claim seam. This never PATCHes the host row: the kernel verb
 *  performs the membership/authority checks and conditional UPDATE atomically.
 *  A winning claim moves the row from the queue lens into `core.my_work`, so
 *  all three cached answers have to move together. */
export function usePickUpWork() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    // The list renders the verb's named refusal beside the affordance. Mark it
    // handled so the application-wide mutation net does not say it twice.
    meta: { errorHandled: true },
    mutationFn: (v: { entity: string; id: string }) =>
      client.raw().rpcScalar<Row>('pick_up_work', { p_entity_type: v.entity, p_id: v.id }, 'core'),
    onSuccess: (_result, v) => {
      void invalidateEntityRows(qc, v.entity)
      void qc.invalidateQueries({ queryKey: ['work-queues'] })
      void qc.invalidateQueries({ queryKey: ['my-work'] })
    },
  })
}

/** ── AFD delivery cockpit write-path — the governed afd.* decision RPCs ────────
 *  Each calls a real afd RPC through the raw transport (schema 'afd') and, on
 *  success, invalidates every ['related','afd',…] read so the cockpit reflects the
 *  new state. Errors (e.g. a pass refused with incomplete mandatory items) reject
 *  the mutation — the caller surfaces `mutation.error.message` as the blocked reason. */
function useAfdWrite<V>(fn: string, buildArgs: (v: V) => Row) {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: V) => client.raw().rpcScalar<unknown>(fn, buildArgs(v), 'afd'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['related', 'afd'] })
      // #2021: the phase ladder no longer comes from a `related` read — it
      // comes from the governed door `afd.my_project_phases`, under its own
      // key. Invalidating only the old one left an advanced phase showing the
      // phase it advanced FROM until the page was reloaded.
      void qc.invalidateQueries({ queryKey: ['afd', 'project-phases'] })
    },
  })
}

/** afd.initiate_gate(p_gate_id) — Flow Facilitator / Outcome Owner open a review. */
export function useInitiateGate() {
  return useAfdWrite<{ gateId: string }>('initiate_gate', (v) => ({ p_gate_id: v.gateId }))
}

/** afd.decide_gate(p_gate_id, p_decision, p_notes) — Outcome Owner records the gate
 *  decision; a `pass` is refused while mandatory checklist items are open. */
export function useDecideGate() {
  return useAfdWrite<{ gateId: string; decision: 'pass' | 'conditional' | 'recycle' | 'reject'; notes?: string }>(
    'decide_gate', (v) => ({ p_gate_id: v.gateId, p_decision: v.decision, p_notes: v.notes ?? null }))
}

/** afd.advance_phase(p_project_id) — the governed phase transition (refused unless
 *  the current phase's exit gate passed/conditional). Returns the next phase key. */
export function useAdvancePhase() {
  return useAfdWrite<{ projectId: string }>('advance_phase', (v) => ({ p_project_id: v.projectId }))
}

/** afd.snapshot_health(p_project_id, p_reason) — freeze an immutable health snapshot. */
export function useSnapshotHealth() {
  return useAfdWrite<{ projectId: string; reason?: string }>(
    'snapshot_health', (v) => ({ p_project_id: v.projectId, p_reason: v.reason ?? 'manual' }))
}

/** afd.set_checklist_item(p_item_id, p_status) — complete/re-open a gate checklist item. */
export function useSetChecklistItem() {
  return useAfdWrite<{ itemId: string; status: string }>(
    'set_checklist_item', (v) => ({ p_item_id: v.itemId, p_status: v.status }))
}

/**
 * service.resolve_case(p_case_id) — the governed transition to resolved.
 *
 * No `onError` here ON PURPOSE (#1581). A hook-level `onError` lands on
 * `mutation.options`, which `shouldReportWriteFailure` reads as "this surface
 * speaks for itself" and the global net then stays silent. The case panel
 * renders the SERVER's own sentence inline; the net still raises the generic
 * toast for the operator who has already scrolled away from the panel. Two
 * readers, one failure, neither suppressing the other.
 *
 * `meta.what` is the noun the generic toast uses. Without it the last-resort
 * message says "this", which on a support desk mid-incident is not a sentence
 * anybody can act on.
 */
export function useResolveCase() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { what: 'this case' },
    mutationFn: (caseId: string) => client.raw().rpcScalar('resolve_case', { p_case_id: caseId }, 'service'),
    onSuccess: () => { void invalidateEntityRows(qc, 'service_case'); invalidateServiceDesk(qc) },
  })
}

/**
 * service.start_work(p_case_id) — the governed way ONTO a case nobody has
 * started (#1581, service 2026082820).
 *
 * `service_case`'s machine declares `new -> open` and does NOT declare
 * `new -> resolved`, so before this verb existed the only route out of the
 * initial state was a raw table update: permitted by the entity's `direct` write
 * door, attributed to nobody, and unreachable from any console surface. Both
 * ends of the move are read from the registered machine inside the RPC, so this
 * hook does not name a target state either.
 */
export function useStartWork() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { what: 'this case' },
    mutationFn: (caseId: string) => client.raw().rpcScalar('start_work', { p_case_id: caseId }, 'service'),
    onSuccess: () => { void invalidateEntityRows(qc, 'service_case'); invalidateServiceDesk(qc) },
  })
}

/* ── The support desk (service 0010, #337) ─────────────────────────────────────
 * Everything below reads the desk's own governed doors rather than assembling an
 * answer in the browser. The queue ORDER in particular is the server's: it comes
 * off `service.sla_clock`, whose due_at the case trigger derives and solely owns.
 * Sorting cases by a deadline recomputed here would be a second implementation of
 * that derivation, and it could only ever order the page it was handed. */

/** Every desk read at once — the queues, their counts and a case's own clocks. */
export function invalidateServiceDesk(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey as unknown[]
      return typeof k[0] === 'string' && (k[0] as string).startsWith('service-')
    },
  })
}

export type CaseQueueKey = 'mine' | 'team' | 'unassigned'
export interface QueueCase {
  case_id: string; subject: string; account_id: string | null; status: string; priority: string
  owner_id: string | null; team_id: string | null; queue: string
  sla_key: string | null; sla_label: string | null; obligation: string | null
  due_at: string | null; breached: boolean; at_risk: boolean; time_remaining: string | null
  first_responded_at: string | null; responses: number; created_at: string
}

/** service.case_queue(queue) — one queue's open cases, soonest deadline first. */
export function useCaseQueue(queue: CaseQueueKey | null) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['service-queue', queue],
    enabled: queue != null,
    queryFn: () => client.raw().rpcTable<QueueCase>('case_queue', { p_queue: queue }, 'service'),
  })
}

export interface QueueCount { queue: string; total: number; breached: number; at_risk: number }

/** service.queue_counts() — how loaded each queue is, breaches counted apart. */
export function useQueueCounts() {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['service-queue-counts'],
    queryFn: () => client.raw().rpcTable<QueueCount>('queue_counts', {}, 'service'),
  })
}

export interface SlaClock {
  clock_id: string; case_id: string; subject: string; sla_key: string | null; sla_label: string | null
  obligation: string; started_at: string; due_at: string | null; status: string
  breached: boolean; time_remaining: string | null
  satisfied_at: string | null; satisfied_late: boolean | null; alert_stage: number
  reaction_outcome: string | null; reaction_at: string | null
}

/** service.sla_board(case_id) — the case's own clocks, breach computed live. */
export function useSlaBoard(caseId?: string) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['service-sla-board', caseId ?? ''],
    enabled: Boolean(caseId),
    queryFn: () => client.raw().rpcTable<SlaClock>('sla_board', { p_case_id: caseId }, 'service'),
  })
}

export interface CaseResponse {
  id: string; case_id: string; channel: string; body: string | null
  responded_at: string; recorded_at: string; responded_by: string | null
}

/** The recorded thread on a case — append-only evidence, newest last. */
export function useCaseResponses(caseId?: string) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['service-responses', caseId ?? ''],
    enabled: Boolean(caseId),
    queryFn: async () =>
      (await client.raw().select<CaseResponse>('service', 'case_response', { where: { case_id: caseId }, limit: 200 }))
        .sort((a, b) => (a.responded_at < b.responded_at ? -1 : 1)),
  })
}

/** service.record_response(case, body, channel) — the governed observation.
 *  `channel` is a vocabulary the database enforces (0010): portal | email |
 *  phone. A rejection here is the server refusing, not a client-side guess. */
export function useRecordResponse() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { caseId: string; body: string; channel: 'portal' | 'email' | 'phone' }) =>
      client.raw().rpcScalar<string>('record_response',
        { p_case_id: v.caseId, p_body: v.body, p_channel: v.channel }, 'service'),
    onSuccess: () => { invalidateServiceDesk(qc); void invalidateEntityRows(qc, 'service_case') },
  })
}

export interface KbHit { id: string; title: string; status: string; snippet: string | null }

/** service.search_kb(q) — the KB deflection surface. Deliberately the SAME door
 *  the copilot's `search_kb` tool binds to: one ranked search (core.text_rank
 *  since 0006), not a second one written for the case panel. */
export function useKbSuggestions(query: string | undefined, limit = 3) {
  const { client } = useSolum()
  const q = (query ?? '').trim()
  return useQuery({
    queryKey: ['service-kb', q, limit],
    enabled: q.length > 2,
    queryFn: () => client.raw().rpcTable<KbHit>('search_kb', { p_query: q, p_limit: limit }, 'service'),
  })
}

/**
 * core.describe_status_machine(p_machine) — a lifecycle as DATA: the states (with
 * category · tone · sort) and the legal transition edges.
 *
 * The triage board is the first console surface to read it, and reads it for the
 * two things a board must not hardcode: WHICH COLUMNS EXIST and WHICH DROPS ARE
 * LEGAL. A tenant renaming `ready_to_verify` to "QA" (the states are renamable by
 * design, core 0046) changes the board's headings without a deploy; removing an
 * edge removes a move from every card's menu.
 */
export interface StatusState { code: string; label: string; category: string; tone: string | null; sort: number; is_initial?: boolean; is_locked?: boolean }
export interface StatusMachine {
  machine: string; entity: string; label: string; states: StatusState[]
  transitions: { from: string; to: string; reason_required?: boolean }[]
  /** The column the lifecycle governs (`core.status_machine.status_column`, 0074). */
  statusColumn?: string
}
export function useStatusMachine(machine: string) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['status-machine', machine],
    // The lifecycle changes when someone re-registers it, i.e. on a deploy —
    // not between two drags of the same card.
    staleTime: 5 * 60_000,
    queryFn: () => client.raw().rpcScalar<StatusMachine | null>('describe_status_machine', { p_machine: machine }, 'core'),
  })
}

/**
 * The status machine GOVERNING AN ENTITY, if it has one — the states a column ⋮
 * filter offers on a status column (#959).
 *
 * `describe_status_machine` is keyed by MACHINE, and nothing in the field
 * dictionary says which machine a column belongs to — so the link is read from
 * `core.status_machine` itself, which is metamodel and which every authenticated
 * session may read (`sm_read`, core 0046). Its `status_column` is the column the
 * lifecycle governs; that is how a filter on `status` gets the machine's OWN
 * states — including a tenant's renames — rather than a list somebody typed.
 *
 * It must never be the reason a list fails. `enabled` is the entity, the query
 * swallows a deployment that predates the machine (or a PostgREST that does not
 * expose it), and the caller falls back to `field_meta.options`. A registry read
 * decorates the filter menu; it is not the list.
 */
export function useEntityStatusMachine(entity?: string) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['entity-status-machine', entity ?? '*'],
    enabled: !!entity,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<StatusMachine | null> => {
      try {
        const rows = await client.raw().select<Row>('core', 'status_machine', { where: { entity_type: entity! } })
        const m = rows[0]
        if (!m) return null
        const full = await client.raw().rpcScalar<StatusMachine | null>(
          'describe_status_machine', { p_machine: String(m.machine) }, 'core')
        // `status_column` lives on the table, not in describe_status_machine's
        // document — carried across so the caller can tell WHICH column it governs.
        return full ? { ...full, statusColumn: String(m.status_column ?? 'status') } as StatusMachine : null
      } catch {
        return null
      }
    },
  })
}

/**
 * feedback.advance_capture(p_capture_id, p_to, p_reason, p_duplicate_of) — the
 * ONLY door the board moves a card through.
 *
 * Deliberately not a `saveEntity({status})`: the RPC calls core.assert_transition,
 * and since feedback 0008 so does a trigger on the table, so an illegal move is
 * refused by the database whichever way it is attempted. The board asks the
 * lifecycle which moves to OFFER; the database decides which it ACCEPTS.
 */
export function useAdvanceCapture() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    // Reported per call via `mutate(vars, { onError })` — a callback on the
    // observer, which the mutation-cache net cannot see. Declared here so the
    // net does not say it a second time (#877).
    meta: { errorHandled: true },
    mutationFn: (v: { id: string; to: string; reason?: string }) =>
      client.raw().rpcScalar('advance_capture',
        { p_capture_id: v.id, p_to: v.to, p_reason: v.reason ?? null }, 'feedback'),
    onSuccess: () => { void invalidateEntityRows(qc, 'feedback_capture') },
  })
}

/**
 * `feedback.capture_attachment` for a KNOWN SET of captures — the screenshots.
 *
 * By id, in one request, rather than a page of the child table filtered in the
 * browser (TEC-02): a board showing 60 captures must not decide which of them
 * have screenshots from the first 500 attachment rows in the deployment.
 */
export function useCaptureAttachments(captureIds: string[]) {
  const { client } = useSolum()
  const ids = useMemo(() => [...new Set(captureIds)].sort(), [captureIds])
  return useQuery({
    queryKey: ['capture-attachments', ids],
    enabled: ids.length > 0,
    queryFn: () => client.raw().select<Row>('feedback', 'capture_attachment', {
      filters: [{ key: 'capture_id', value: `in.(${ids.join(',')})` }],
      order: 'created_at.asc',
      limit: 500,
    }),
  })
}

/** contracts.activate_agreement / terminate_agreement — the governed lifecycle moves. */
export function useAgreementLifecycle() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { id: string; action: 'activate' | 'terminate' }) =>
      client.raw().rpcScalar(v.action === 'activate' ? 'activate_agreement' : 'terminate_agreement',
        v.action === 'activate' ? { p_agreement_id: v.id } : { p_agreement_id: v.id, p_reason: null }, 'contracts'),
    onSuccess: () => { void invalidateEntityRows(qc, 'contracts_agreement') },
  })
}

/** commerce.advance_order(p_order_id, p_to) — the governed order transition. */
export function useAdvanceOrder() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { id: string; to: string }) => client.raw().rpcScalar('advance_order', { p_order_id: v.id, p_to: v.to }, 'commerce'),
    onSuccess: () => { void invalidateEntityRows(qc, 'commerce_order') },
  })
}

/** commerce.request_full_return(p_order_id) — raise a whole-order return (0002). */
export function useRequestReturn() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orderId: string) => client.raw().rpcScalar<string>('request_full_return', { p_order_id: orderId }, 'commerce'),
    onSuccess: () => { void invalidateEntityRows(qc, 'commerce_return'); void invalidateEntityRows(qc, 'commerce_order') },
  })
}

/** commerce.advance_return(p_return_id, p_to) — the governed return transition (0002). */
export function useAdvanceReturn() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { id: string; to: string }) => client.raw().rpcScalar('advance_return', { p_return_id: v.id, p_to: v.to }, 'commerce'),
    onSuccess: () => { void invalidateEntityRows(qc, 'commerce_return'); void invalidateEntityRows(qc, 'commerce_order') },
  })
}

/** The notification spine (core 0052). One shared feed for the NotificationCenter
 *  bell; reads are RLS-scoped to the caller, so `select` returns only my rows. */
export interface Notification {
  id: string; kind: string; title: string; body: string | null; action_url: string | null
  priority: 'low' | 'normal' | 'high'; read_at: string | null; created_at: string
}
export function useNotifications() {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['notifications'],
    // newest-first, client-side (order isn't a transport option; the list is capped small).
    queryFn: async () => (await client.raw().select<Notification>('core', 'notification', { limit: 50 }))
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    refetchInterval: 60_000,   // gentle poll; the relay push channel is slice 2
  })
}
export function useMarkNotificationRead() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => client.raw().rpcScalar('mark_notification_read', { p_id: id }, 'core'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notifications'] }) },
  })
}
export function useMarkAllNotificationsRead() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => client.raw().rpcScalar<number>('mark_all_notifications_read', {}, 'core'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notifications'] }) },
  })
}

/** afd.set_artifact_status(p_id, p_status) — advance a Context Substrate artifact. */
export function useSetArtifactStatus() {
  return useAfdWrite<{ id: string; status: string }>(
    'set_artifact_status', (v) => ({ p_id: v.id, p_status: v.status }))
}

/** afd.submit_feedback(...) — capture a phase feedback form (fields rendered via <Form>). */
export function useSubmitFeedback() {
  return useAfdWrite<{ projectId: string; phase: string; variant: string; answers: Record<string, unknown> }>(
    'submit_feedback', (v) => ({
      p_project_id: v.projectId, p_phase: v.phase, p_variant: v.variant,
      p_type: (v.answers.type as string) ?? null,
      p_title: String(v.answers.text ?? 'Feedback').slice(0, 60),
      p_content: (v.answers.text as string) ?? null,
      p_answers: v.answers,
      p_priority: (v.answers.priority as string) ?? null,
      p_severity: (v.answers.severity as string) ?? null,
      p_source: 'review',
    }))
}

/** ── CPQ quote-builder write-path — the governed cpq.* RPCs behind the deal desk ──
 *  Each mutation, on success, invalidates the quote header (row + rows) and its line
 *  reads (['related','cpq','quote_line',…]) so the builder's totals and grid refresh.
 *  These are the doors the DB enforces — the same RPCs the module's tests exercise. */
function useCpqInvalidate(quoteId: string) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['row', 'cpq_quote', quoteId] })
    void invalidateEntityRows(qc, 'cpq_quote')
    void qc.invalidateQueries({ queryKey: ['related', 'cpq', 'quote_line'] })
  }
}

/** cpq.add_line — a custom (ad-hoc) line: no product, the caller supplies a
 *  description + unit price (the donor's custom-line path). Refused on a non-draft
 *  quote by the RPC. Returns the new line id. */
export function useAddCustomLine(quoteId: string) {
  const { client } = useSolum()
  const invalidate = useCpqInvalidate(quoteId)
  return useMutation({
    mutationFn: (v: { description: string; qty: number; unitPrice: number; discountPct?: number }) =>
      client.raw().rpcScalar<string>('add_line', {
        p_quote_id: quoteId, p_product_id: null, p_qty: v.qty,
        p_description: v.description, p_unit_price: v.unitPrice, p_discount_pct: v.discountPct ?? 0,
      }, 'cpq'),
    onSuccess: invalidate,
  })
}

/** Edit a quote line (qty / unit price). cpq ships NO dedicated update-line RPC, so
 *  this patches the line row through the governed table write — the
 *  line_reject_when_locked trigger still refuses a locked quote — then calls
 *  cpq.recalc(p_quote_id) to roll the change up into the quote totals. */
export function useUpdateLine(quoteId: string) {
  const { client } = useSolum()
  const invalidate = useCpqInvalidate(quoteId)
  return useMutation({
    mutationFn: async (v: { lineId: string; patch: Row }) => {
      await client.raw().update('cpq', 'quote_line', v.lineId, v.patch)
      return client.raw().rpcScalar<number>('recalc', { p_quote_id: quoteId }, 'cpq')
    },
    onSuccess: invalidate,
  })
}

/** Remove a quote line — table delete (the locked-quote trigger still guards) then
 *  cpq.recalc to refresh totals. (Same RPC gap as useUpdateLine: no remove_line RPC.) */
export function useRemoveLine(quoteId: string) {
  const { client } = useSolum()
  const invalidate = useCpqInvalidate(quoteId)
  return useMutation({
    mutationFn: async (lineId: string) => {
      await client.raw().delete('cpq', 'quote_line', lineId)
      return client.raw().rpcScalar<number>('recalc', { p_quote_id: quoteId }, 'cpq')
    },
    onSuccess: invalidate,
  })
}

/** cpq.revise_quote — the immutable-snapshot versioning move: copies the quote
 *  forward as a new draft version and supersedes this one. Returns the NEW quote id
 *  (the caller navigates to it). */
export function useReviseQuote(quoteId: string) {
  const { client } = useSolum()
  const invalidate = useCpqInvalidate(quoteId)
  return useMutation({
    mutationFn: () => client.raw().rpcScalar<string>('revise_quote', { p_quote_id: quoteId }, 'cpq'),
    onSuccess: invalidate,
  })
}

/** ── Catalog & Pricing workspace reads/writes ─────────────────────────────────
 *  Products ride the generic entity path (useEntityRows/useSaveEntity on
 *  'catalog_product'). Price lists, prices (volume tiers) and the product-kind
 *  catalog live on the catalog schema but are NOT registered as browsable console
 *  entities, so they go through the raw transport (RLS still applies). The unit
 *  price is resolved read-only by the catalog.unit_price() RPC — the ONE place
 *  pricing logic lives, so the workspace never re-implements tier selection. */

/** The product-kind catalog (catalog.product_kind) — kinds are extensible DATA
 *  carrying behaviour (is_stockable / is_recurring). Read-only reference. */
export function useCatalogKinds() {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['catalog', 'product_kind'],
    queryFn: () => client.raw().select('catalog', 'product_kind', { limit: 100 }),
  })
}

/** All price lists (catalog.price_list) — standard / volume / contract books. */
export function usePriceLists() {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['catalog', 'price_list'],
    queryFn: () => client.raw().select('catalog', 'price_list', { limit: 200 }),
  })
}

/** The prices (volume tiers via min_qty) in one price list. */
export function useCatalogPrices(priceListId?: string) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['catalog', 'price', priceListId ?? ''],
    enabled: Boolean(priceListId),
    queryFn: () => client.raw().select('catalog', 'price', { where: { price_list_id: priceListId! }, limit: 500 }),
  })
}

/** Create (id 'new') or patch a price/tier; invalidates every catalog.price read. */
export function useSaveCatalogPrice() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Row }) =>
      id === 'new' ? client.raw().insert('catalog', 'price', patch) : client.raw().update('catalog', 'price', id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'price'] }),
  })
}

/** The derived unit price for a product+qty in a list — the governed
 *  catalog.unit_price() resolver (highest min_qty tier the qty satisfies), read-only. */
export function useUnitPrice(productId?: string, qty = 1, priceListKey?: string) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['catalog', 'unit_price', productId ?? '', qty, priceListKey ?? ''],
    enabled: Boolean(productId),
    queryFn: () => client.raw().rpcScalar<number | null>('unit_price',
      { p_product_id: productId, p_qty: qty, p_price_list: priceListKey ?? null }, 'catalog'),
  })
}

/** ── Scheduler / Relay ops reads — the "did the motor turn?" surface ──────────
 *  Ops-only, admin-RLS core tables read through the raw transport:
 *   • core.scheduled_job (0049) — the job registry the worker polls (due_jobs()).
 *     The ops view reads the WHOLE registry, not just the due subset, so an
 *     operator sees errored + never-run rows too.
 *   • the event spine (0014) — deliveries joined client-side to their subscription
 *     (subscriber/handler) + event (topic), then filtered to the failing/dead rows.
 *     A status='dead' delivery is a dead-letter — an ops incident. (There is no
 *     purpose-built ops-read RPC; the view composes the governed reads, mirroring
 *     the approvals inbox — see the delivery report's gap note.) */
export interface ScheduledJob {
  key: string; module: string; rpc: string; cadence_seconds: number; enabled: boolean
  last_run_at: string | null; last_status: string | null; last_error: string | null; next_run_at: string | null
}

export function useScheduledJobs() {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['ops', 'scheduled_job'],
    queryFn: () => client.raw().select<ScheduledJob>('core', 'scheduled_job', { limit: 500 }),
  })
}

interface EventRow { id: string; topic: string; subject_type: string | null; subject_id: string | null; at: string }
interface SubscriptionRow { id: string; subscriber: string; handler: string; kind: string }
interface DeliveryRow {
  id: string; event_id: string; subscription_id: string; status: string; attempts: number
  last_error: string | null; next_attempt_at: string | null; created_at: string
}
export interface EgressDelivery extends DeliveryRow {
  topic: string | null; subscriber: string | null; handler: string | null
}

/** Failing + dead webhook deliveries, joined client-side to subscription + event. */
export function useEgressDeliveries() {
  const { client } = useSolum()
  const raw = client.raw()
  return useQuery({
    queryKey: ['ops', 'event_delivery'],
    queryFn: async (): Promise<EgressDelivery[]> => {
      const [deliveries, subs, events] = await Promise.all([
        raw.select<DeliveryRow>('core', 'event_delivery', { limit: 500 }),
        raw.select<SubscriptionRow>('core', 'event_subscription', { limit: 500 }),
        raw.select<EventRow>('core', 'event', { limit: 500 }),
      ])
      const subById = new Map(subs.map((s) => [s.id, s]))
      const evById = new Map(events.map((e) => [e.id, e]))
      return deliveries
        .filter((d) => d.status === 'failed' || d.status === 'dead')
        .map((d) => ({
          ...d,
          topic: evById.get(d.event_id)?.topic ?? null,
          subscriber: subById.get(d.subscription_id)?.subscriber ?? null,
          handler: subById.get(d.subscription_id)?.handler ?? null,
        }))
        // dead-letters first (incidents), then most-recently created
        .sort((a, b) => Number(b.status === 'dead') - Number(a.status === 'dead') || b.created_at.localeCompare(a.created_at))
    },
  })
}

/** The ONE governed recovery act for a dead-letter (#1593): core.requeue_dead_delivery.
 *  Platform-admin only — the server asserts it (assert_platform_admin in body;
 *  service_role revoked by name) — and it requires an operator reason and an active
 *  subscription. The act keeps the CUMULATIVE attempt count, so a failed re-attempt
 *  returns straight to dead: one attempt per operator decision, never a fresh
 *  five-attempt storm per click. On success the ops read is invalidated — the row
 *  leaves the failed/dead filter (or comes back dead) and the board tells the truth. */
export function useRequeueDelivery() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    // Reported per call inside the confirm dialog (the server's own refusal words),
    // so the application-wide mutation net must not say it twice (#877).
    meta: { errorHandled: true },
    mutationFn: (v: { id: string; reason: string }) =>
      client.raw().rpcScalar<unknown>('requeue_dead_delivery',
        { p_delivery_id: v.id, p_reason: v.reason }, 'core'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ops', 'event_delivery'] }) },
  })
}

/** Save = create (id 'new') or patch. Invalidates the list + this row on success. */
export function useSaveEntity(entity: string) {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Row }) => {
      const handle = await client.entity(entity)
      if (!handle) throw new Error(`unknown entity ${entity}`)
      return id === 'new' ? handle.create(patch) : handle.update(id, patch)
    },
    /* Every surface a saved record can appear in, not the three this reached
       (#2018). A title may be a composed expression over any field, and a
       reference label on ANOTHER record renders this one by name — so both
       families go, along with the aggregates that can count it. */
    /* RETURNED, so `mutateAsync` resolves only once the surfaces it just
       invalidated have refetched. A caller that closes a form on this promise
       is closing over a grid that agrees with the server (#2000 audit). */
    onSuccess: (_result, v) =>
      invalidateRecordSurfaces(qc, entity, v.id === 'new' ? undefined : v.id),
  })
}

/**
 * `core.titles_of` — resolve a heterogeneous list of `(entity_type, id)` refs to
 * TITLES, in one round trip (#1472, ADR F-33.3).
 *
 * This is the door `useLookupLabels` above could not be. That hook resolves ONE
 * entity type per call, so a panel naming a mixed list of edges has to know
 * every type it might meet and fan out per type — which is why the Connections
 * panel resolved three hardcoded types and rendered a raw uuid for the rest.
 *
 * The title is DATA (`core.entity_title`, declared by the module that owns the
 * entity), not a column-name guess: measured, 120 of 262 registered entities
 * have no column called name/title/label/display_name at all, so a guess is
 * wrong for nearly half the fleet.
 *
 * The door is SECURITY INVOKER, so it resolves under this session's own RLS and
 * returns NOTHING for a record this user cannot see — indistinguishably from an
 * id that does not exist and from an entity with no declaration. So `title()`
 * falling back is an ordinary case, not an error; and the fallback must never be
 * the uuid, which tells a person nothing and is exactly what #1472 exists to
 * stop.
 */
export function useRecordTitles(refs: { type: string; id: string }[]) {
  const { client } = useSolum()
  // Sorted + deduped so the query key is stable across renders that reorder the
  // same set — otherwise every re-render is a cache miss and a fresh round trip
  // (the same reason useLookupLabels does it).
  const wanted = useMemo(() => {
    const set = new Set<string>()
    for (const r of refs) if (r.type && r.id) set.add(`${r.type} ${r.id}`)
    return [...set].sort().map((k) => {
      const [type, id] = k.split(' ')
      return { type: type!, id: id! }
    })
  }, [refs])

  const q = useQuery({
    queryKey: ['record-titles', wanted],
    enabled: wanted.length > 0,
    queryFn: () => client.raw().rpcTable<TitleRow>('titles_of', { p_refs: wanted }, 'core'),
  })

  /* A FAILED read exposes no rows — not the ones a previous success left in the
     cache. TanStack keeps `data` when a refetch fails; showing it would carry a
     former title or subtitle through an outage or a withdrawn grant (#2757 R2,
     ROOT #2425 c.118656 §2). The next successful answer repopulates it. */
  const byRef = useMemo(
    () => new Map((q.isError ? [] : q.data ?? []).map((r) => [`${r.entity_type}:${r.id}`, r])),
    [q.data, q.isError])

  /** Title for a ref — `fallback` while in flight, failed, undeclared, or not visible. */
  const title = useMemo(() => (type: string, id: unknown, fallback = '—'): string => {
    if (id == null || id === '') return fallback
    return byRef.get(`${type}:${String(id)}`)?.title ?? fallback
  }, [byRef])

  const subtitle = useMemo(() => (type: string, id: unknown): string | null => {
    if (id == null || id === '') return null
    return byRef.get(`${type}:${String(id)}`)?.subtitle ?? null
  }, [byRef])

  return { title, subtitle, byRef, query: q }
}

export interface TitleRow {
  entity_type: string
  id: string
  title: string | null
  subtitle: string | null
}

/**
 * `titles_of` plus the OWNING ACCOUNT — `core.recall_contexts` (#2350 §9).
 *
 * A superset of `TitleRow`, deliberately, because the surface that needs the
 * account needs the title beside it: resolving the two through separate round
 * trips would let them disagree about which rows exist, and the door would then
 * be answering about a set the caller never sees.
 */
export interface RecallRow extends TitleRow {
  /** The governed account id, derived from the tenancy registry. Null for a
   *  `global`/`unset` entity, an unattributed row, or a parent the caller
   *  cannot read. It is a GROUPING KEY, never copy. */
  account_id: string | null
  /** Resolved through the same governed title contract. Null — beside a
   *  non-null `account_id` — when the caller may read the record but not the
   *  account it belongs to. */
  account_title: string | null
}

/**
 * The recall door, bounded and caller-RLS (#2350 §9).
 *
 * Deliberately NOT a widening of `useRecordTitles`. The palette, the related
 * lists and the lookup labels want a name and nothing else, and making every
 * one of them pay for an account walk they never render would be the wrong
 * trade — `core.recall_contexts` resolves one account statement per entity type
 * present in the result, which is cheap for a drawer and pointless for a cell.
 * Its own query key for the same reason: two surfaces asking different
 * questions should not share a cache entry.
 *
 * The 64-ref ceiling is the door's, and the caller cannot exceed it by
 * construction — `MAX_RECENTS` (40) + `MAX_PINS` (24) is exactly what the two
 * governed stores hold. `wanted` is sliced anyway rather than trusted, because
 * "cannot happen" is a claim about today's constants.
 */
export function useRecallContexts(refs: { type: string; id: string }[]) {
  const { client } = useSolum()
  // Sorted + deduped so the key is stable across renders that reorder the same
  // set — the same reason `useRecordTitles` and `useLookupLabels` do it.
  const wanted = useMemo(() => {
    const set = new Set<string>()
    for (const r of refs) if (r.type && r.id) set.add(`${r.type} ${r.id}`)
    return [...set].sort().slice(0, RECALL_CONTEXT_LIMIT).map((k) => {
      const [type, id] = k.split(' ')
      return { type: type!, id: id! }
    })
  }, [refs])

  const q = useQuery({
    queryKey: ['recall-contexts', wanted],
    enabled: wanted.length > 0,
    queryFn: () => client.raw().rpcTable<RecallRow>('recall_contexts', { p_refs: wanted }, 'core'),
  })

  const byRef = useMemo(
    () => new Map((q.data ?? []).map((r) => [`${r.entity_type}:${r.id}`, r])),
    [q.data])

  return { byRef, query: q }
}

/** `MAX_RECENTS` (40) + `MAX_PINS` (24) — the door refuses more. */
export const RECALL_CONTEXT_LIMIT = 64

/**
 * Presentation-safe identity for one record reference (#1705).
 *
 * A UUID is never copy. `core.titles_of` deliberately makes missing, hidden and
 * undeclared records indistinguishable, so the only safe fallback is neutral
 * wording. The entity label is metamodel data; an unknown entity key is not
 * displayed either.
 *
 * ── `present` vs `resolved` (#2325 §3) ─────────────────────────────────────
 * The door has THREE outcomes and this used to collapse them into two.
 *
 *   • a row with a title            — named, and the name is shown
 *   • a row whose title is NULL     — the caller may read the record and its
 *                                     declared title expression produced
 *                                     nothing (an organization saved with no
 *                                     legal or display name, a contact point
 *                                     with neither a label nor a value)
 *   • NO ROW at all                 — invisible, deleted, or of an entity that
 *                                     declares no title. Indistinguishable on
 *                                     purpose (#1472 / ADR F-33)
 *
 * `resolved` answered only "is there a name", so the middle case looked exactly
 * like the third — and a surface that filters unresolved refs out therefore
 * HID a record the person may read and had just opened. `present` is the row's
 * own existence, so the two behaviours the ruling asks for become separable: an
 * authorized record with no resolvable label reads `Unnamed {type}`, and an
 * absent one is dropped without ever showing its former name or its id.
 *
 * The third case — no row — is UNAVAILABLE whatever its reason, including an
 * entity that declares no title: `title_declared` is deliberately not
 * consulted, because a missing declaration is not evidence the caller may read
 * the record (#2757 R2, ROOT #2425 c.118656 §1). The one exception is evidence
 * the CALLER holds: `{ rowsRead: true }` says every ref is a row this caller has
 * just read through its own governed door (a list page), and then a missing
 * titles_of row is `Unnamed {type}`. A FAILED read wins over both, and over
 * any row a previous read left behind (§2; `useRecordTitles` exposes none while
 * it is failed). The enumeration lives in `data/recordIdentity.ts`; this hook
 * binds it to the two governed reads.
 *
 * Note which way each fails. `present` is false for the absent case, so a
 * caller that forgets to check it shows neutral wording rather than a leak; the
 * disclosure risk is in the other direction and there is none — the type name
 * is the caller's own list entry, and the door already refused to say anything
 * about the row.
 */
export function useRecordIdentities(
  refs: { type: string; id: string }[],
  options?: { rowsRead?: boolean },
) {
  const rowsRead = options?.rowsRead === true
  const schema = useSchema()
  const entities = useEntityIdentities()
  const titles = useRecordTitles(refs)
  const entityIdentity = useMemo(
    () => new Map((entities.data ?? []).map((entity) => [entity.entity_type, entity])),
    [entities.data],
  )
  const structuralLabels = useMemo(
    () => new Map((schema.data?.entities ?? []).map((entity) => [entity.key, entity.label])),
    [schema.data],
  )

  const identity = useMemo(() => (type: string, id: unknown) => {
    const key = id == null ? '' : String(id)
    const row = key ? titles.byRef.get(`${type}:${key}`) : undefined
    const entity = entityIdentity.get(type)
    const safeType = entity?.singular_label ?? structuralLabels.get(type) ?? 'Record'
    /* The wording decision is `data/recordIdentity.ts` — four states over a row
       and two booleans, stated where they can be enumerated without a query
       client. `isError` stays true when a REFETCH fails over retained data, and
       that is the case it must win (#2757 R2). */
    const naming = recordNaming({
      row,
      typeLabel: safeType,
      pending: titles.query.isPending,
      failed: titles.query.isError,
      readable: rowsRead,
    })
    return {
      title: naming.title,
      subtitle: row?.subtitle ?? null,
      typeLabel: safeType,
      pluralLabel: entity?.plural_label ?? safeType,
      iconKey: entity?.icon_key ?? 'record',
      /** Which of the four the door and the registry add up to. */
      state: naming.state,
      /** A row a surface may draw. False only for a stale or refused item. */
      present: isListable(naming.state),
      /** …and it carries a governed NAME. `present && !resolved` is `Unnamed`. */
      resolved: naming.state === 'named',
    }
  }, [entityIdentity, structuralLabels, titles.byRef, titles.query.isPending, titles.query.isError, rowsRead])

  const canNavigate = useMemo(() => (type: string): boolean => {
    const contract = entityIdentity.get(type)
    return contract?.navigation_eligible === true
  }, [entityIdentity])

  return { identity, canNavigate, query: titles.query, identityQuery: entities, entityIdentity }
}
